import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  ArrowRight,
  ChevronRight,
  ExternalLink,
  Clock,
  Copy,
  LocateFixed,
  LogOut,
  Mail,
  MapPin,
  Menu as MenuIcon,
  Navigation,
  Phone,
  Search,
  ShoppingBag,
  Star,
  User,
  X
} from "lucide-react";
import {
  cateringPackages,
  contact,
  faqs,
  homeHighlights,
  legalPages,
  locationHours as LOCATION_HOURS,
  menuSections as staticMenuSections,
  navItems,
  routeMeta,
  site
} from "./content/siteContent.js";
import AdminPage from "./AdminPage.jsx";
import {
  DEFAULT_CATERING_ALGORITHM,
  calculateCateringEstimate,
  clampCateringQuote,
  createDefaultCateringQuote,
  normalizeCateringAlgorithm
} from "./catering.js";
import {
  configureMetaTracking,
  createMetaEventId,
  trackMetaEvent
} from "./metaEvents.js";

const ROUTE_EXIT_MS = 180;
const ROUTE_ENTER_MS = 520;
const RECAPTCHA_CATERING_ACTION = "catering_lead";
const RECAPTCHA_SUBSCRIBER_ACTION = "subscriber_signup";
const ADMIN_LOGOUT_EVENT = "gelato-admin-logout";
const STATIC_MENU_SECTIONS = staticMenuSections;
const STATIC_SEARCHABLE_MENU_SECTIONS = STATIC_MENU_SECTIONS;
const MENU_SECTION_CHAPTERS = {
  gelato: {
    title: "Gelato",
    eyebrow: "Italian classics",
    image: site.assets.cone,
    tone: "gelato"
  },
  beverages: {
    title: "Beverages",
    eyebrow: "Sips and coffee",
    image: site.assets.beveragePour,
    tone: "beverages"
  },
  "happy-hour": {
    title: "Happy Hour",
    eyebrow: "Tuesday through Friday",
    image: site.assets.cocktail,
    tone: "happy-hour"
  }
};
const HAPPY_HOUR_DRINK_MATCHERS = [
  "beer",
  "wine",
  "mule",
  "old fashioned",
  "whiskey",
  "cocktail",
  "daiquiri",
  "paloma",
  "gin and tonic",
  "gin",
  "tonic"
];

let appConfigPromise;
let recaptchaScriptPromise;
let cateringAlgorithmPromise;

async function loadAppConfig() {
  if (!appConfigPromise) {
    appConfigPromise = fetch("/api/config").then(async (response) => {
      if (!response.ok) {
        throw new Error("Unable to load site configuration.");
      }

      return response.json();
    });
  }

  return appConfigPromise;
}

function loadRecaptchaScript(siteKey) {
  if (!siteKey || typeof window === "undefined") {
    return Promise.reject(new Error("Verification is not configured."));
  }

  if (window.grecaptcha?.execute) {
    return Promise.resolve(window.grecaptcha);
  }

  if (!recaptchaScriptPromise) {
    recaptchaScriptPromise = new Promise((resolve, reject) => {
      const existingScript = document.querySelector("script[data-recaptcha-v3]");

      if (existingScript) {
        existingScript.addEventListener("load", () => resolve(window.grecaptcha), { once: true });
        existingScript.addEventListener("error", () => reject(new Error("Verification could not load.")), { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
      script.async = true;
      script.defer = true;
      script.dataset.recaptchaV3 = "true";
      script.addEventListener("load", () => resolve(window.grecaptcha), { once: true });
      script.addEventListener("error", () => reject(new Error("Verification could not load.")), { once: true });
      document.head.appendChild(script);
    });
  }

  return recaptchaScriptPromise;
}

async function executeRecaptcha(siteKey, action) {
  const grecaptcha = await loadRecaptchaScript(siteKey);

  return new Promise((resolve, reject) => {
    grecaptcha.ready(() => {
      grecaptcha
        .execute(siteKey, { action })
        .then(resolve)
        .catch(() => reject(new Error("Verification failed. Please try again.")));
    });
  });
}

async function loadCateringAlgorithm() {
  if (!cateringAlgorithmPromise) {
    cateringAlgorithmPromise = fetch("/api/content/catering-algorithm")
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Unable to load catering settings.");
        }

        return response.json();
      })
      .then((payload) => normalizeCateringAlgorithm(payload.algorithm));
  }

  return cateringAlgorithmPromise;
}

function normalizePath(pathname) {
  if (!pathname || pathname === "/") {
    return "/";
  }

  return pathname.endsWith("/") ? pathname : `${pathname}/`;
}

function pathnameFromHref(href) {
  return String(href || "").split(/[?#]/)[0] || "/";
}

function menuSearchFromRoute(search = "") {
  return new URLSearchParams(search).get("search") || "";
}

function slugify(value) {
  return value.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function sectionSlug(section) {
  return section?.slug || slugify(section?.title || "");
}

function menuItemSlug(item) {
  return item?.slug || slugify(item?.name || "");
}

function menuItemPath(section, item) {
  if (item?.canonicalPath) {
    return item.canonicalPath;
  }

  return `/menu/${sectionSlug(section)}/${menuItemSlug(item)}/`;
}

function isSecretMenuSection(section) {
  return Boolean(section?.isSecret || sectionSlug(section) === "secret-menu");
}

function sectionStatusLabel(section) {
  return section?.isStatusActive && section?.statusLabel ? section.statusLabel : "";
}

function isSecretMenuItem(section, item) {
  return Boolean(isSecretMenuSection(section) || item?.isSecret);
}

function isSearchOnlyMenuItem(section, item) {
  return Boolean(isSecretMenuSection(section) || item?.isSecret);
}

function isOutOfSeasonMenuItem(item) {
  return Boolean(item?.isOutOfSeason);
}

function isInactiveMenuItem(item) {
  return item?.isActive === false;
}

function isUnavailableMenuItem(item) {
  return Boolean(isOutOfSeasonMenuItem(item) || isInactiveMenuItem(item));
}

function isActiveMenuItem(item) {
  return item?.isActive !== false;
}

function publicMenuSections(sections) {
  return sections
    .map((section) => ({
      ...section,
      items: section.items || []
    }))
    .filter((section) => section.items.length);
}

function visibleMenuSections(sections) {
  return publicMenuSections(sections)
    .filter((section) => !isSecretMenuSection(section))
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !isSearchOnlyMenuItem(section, item) && isActiveMenuItem(item))
    }))
    .filter((section) => section.items.length);
}

function searchableMenuSections(sections) {
  return publicMenuSections(sections).filter((section) => !isSecretMenuSection(section));
}

function menuItemSearchText(section, item) {
  return [
    section.title,
    section.note,
    item.name,
    item.description,
    item.price,
    ...(item.tags || [])
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

function flattenMenuItems(sections) {
  return sections.flatMap((section) =>
    section.items.map((item) => ({
      section,
      item,
      sectionSlug: sectionSlug(section),
      itemSlug: menuItemSlug(item),
      path: menuItemPath(section, item)
    }))
  );
}

function menuItemRecordId(item) {
  return item?.id == null || String(item.id).startsWith("fallback-") ? "" : String(item.id);
}

function menuItemCanonicalId(item) {
  return item?.canonicalItemId ? String(item.canonicalItemId) : "";
}

function findMenuItemContextPlacements(entry, sections) {
  if (!entry) {
    return [];
  }

  const currentItemId = menuItemRecordId(entry.item);
  const currentCanonicalId = menuItemCanonicalId(entry.item);
  const targetCanonicalId = currentCanonicalId || currentItemId;

  if (!targetCanonicalId) {
    return [];
  }

  return flattenMenuItems(sections)
    .filter((itemEntry) => menuItemRecordId(itemEntry.item) !== currentItemId)
    .filter((itemEntry) => {
      const placementCanonicalId = menuItemCanonicalId(itemEntry.item);
      const placementItemId = menuItemRecordId(itemEntry.item);

      return placementCanonicalId === targetCanonicalId || placementItemId === targetCanonicalId;
    })
    .filter((itemEntry) => !isSearchOnlyMenuItem(itemEntry.section, itemEntry.item));
}

function findMenuItemByPath(path, sections) {
  const match = normalizePath(path).match(/^\/menu\/([^/]+)\/([^/]+)\/$/);

  if (!match) {
    return null;
  }

  const [, currentSectionSlug, currentItemSlug] = match;

  return (
    flattenMenuItems(sections).find(
      (entry) => entry.sectionSlug === currentSectionSlug && entry.itemSlug === currentItemSlug
    ) || null
  );
}

function menuItemFallbackDescription(entry) {
  const sectionTitle = entry.section.title.toLowerCase();

  return `${entry.item.name} is part of the ${sectionTitle} menu at The Gelato Spot, a Phoenix restaurant at 32nd Street and Shea serving wood fired pizza, gelato, panini, salads, coffee, wine, and catering.`;
}

function menuItemPageDescription(entry) {
  const itemDescription = entry.item.description?.trim();

  if (itemDescription) {
    return `${entry.item.name}: ${itemDescription} Available from The Gelato Spot at 32nd Street and Shea in Phoenix.`;
  }

  return menuItemFallbackDescription(entry);
}

function menuItemMeta(entry) {
  return {
    title: `${entry.item.name} | ${entry.section.title} Menu | The Gelato Spot Phoenix`,
    description: menuItemPageDescription(entry)
  };
}

function absoluteSiteUrl(value) {
  if (!value) {
    return site.domain;
  }

  if (/^https?:\/\//i.test(value)) {
    return value;
  }

  return `${site.domain}${value.startsWith("/") ? value : `/${value}`}`;
}

function exactMenuPrice(price) {
  const match = String(price || "").trim().match(/^\$(\d+(?:\.\d{2})?)$/);
  return match?.[1] || "";
}

function hasPizzaRangePrice(section, item) {
  const normalizedPrice = String(item?.price || "").replace(/\s/g, "");
  return sectionSlug(section) === "wood-fired-pizza" && ["$13-15", "$13-$15"].includes(normalizedPrice);
}

function isMenuItemPath(path) {
  return /^\/menu\/[^/]+\/[^/]+\/$/.test(normalizePath(path));
}

const PHOENIX_TIME_ZONE = "America/Phoenix";
const LOCATION_COORDS = { lat: 33.5826, lng: -112.0134 };
function minutesFromTime(value) {
  const [hours, minutes] = value.split(":").map(Number);
  return hours * 60 + minutes;
}

function formatMinutes(minutes) {
  const date = new Date(Date.UTC(2026, 0, 1, Math.floor(minutes / 60), minutes % 60));

  return new Intl.DateTimeFormat("en-US", {
    hour: "numeric",
    minute: "2-digit",
    timeZone: "UTC"
  }).format(date);
}

function getPhoenixDayAndMinutes(date = new Date()) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: PHOENIX_TIME_ZONE,
    weekday: "long",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false
  }).formatToParts(date);
  const weekday = parts.find((part) => part.type === "weekday")?.value || "Sunday";
  const hour = Number(parts.find((part) => part.type === "hour")?.value || 0);
  const minute = Number(parts.find((part) => part.type === "minute")?.value || 0);

  return {
    dayIndex: LOCATION_HOURS.findIndex((item) => item.day === weekday),
    minutes: hour * 60 + minute
  };
}

function nextOpenText(dayIndex) {
  for (let offset = 1; offset <= 7; offset += 1) {
    const next = LOCATION_HOURS[(dayIndex + offset) % 7];

    if (!next.closed) {
      return `${next.day} at ${formatMinutes(minutesFromTime(next.open))}`;
    }
  }

  return "";
}

function getOpenStatus(date = new Date()) {
  const { dayIndex, minutes } = getPhoenixDayAndMinutes(date);
  const today = LOCATION_HOURS[dayIndex] || LOCATION_HOURS[0];

  if (today.closed) {
    return {
      state: "closed",
      label: "Closed today",
      detail: `Opens ${nextOpenText(dayIndex)}`
    };
  }

  const open = minutesFromTime(today.open);
  const close = minutesFromTime(today.close);

  if (minutes >= open && minutes < close) {
    return {
      state: "open",
      label: "Open now",
      detail: `Until ${formatMinutes(close)}`
    };
  }

  if (minutes < open) {
    return {
      state: "closed",
      label: "Closed now",
      detail: `Opens today at ${formatMinutes(open)}`
    };
  }

  return {
    state: "closed",
    label: "Closed now",
    detail: `Opens ${nextOpenText(dayIndex)}`
  };
}

function distanceInMiles(from, to) {
  const radiusMiles = 3958.8;
  const toRadians = (value) => (value * Math.PI) / 180;
  const deltaLat = toRadians(to.lat - from.lat);
  const deltaLng = toRadians(to.lng - from.lng);
  const lat1 = toRadians(from.lat);
  const lat2 = toRadians(to.lat);
  const a =
    Math.sin(deltaLat / 2) ** 2 +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLng / 2) ** 2;

  return radiusMiles * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

function isAppleDevice() {
  if (typeof navigator === "undefined") {
    return false;
  }

  const platform = navigator.platform || "";
  const userAgent = navigator.userAgent || "";

  return /Mac|iPhone|iPad|iPod/.test(platform) || /iPhone|iPad|iPod|Macintosh/.test(userAgent);
}

function useMenuSections() {
  const [state, setState] = useState({
    sections: STATIC_MENU_SECTIONS,
    source: "static",
    loading: true,
    error: ""
  });

  useEffect(() => {
    let active = true;

    fetch("/api/content/menu")
      .then(async (response) => {
        if (!response.ok) {
          throw new Error("Unable to load menu.");
        }

        return response.json();
      })
      .then((payload) => {
        if (!active) {
          return;
        }

        const sections = Array.isArray(payload.sections) && payload.sections.length
          ? payload.sections
          : STATIC_MENU_SECTIONS;

        setState({
          sections,
          source: payload.source || "static",
          loading: false,
          error: ""
        });
      })
      .catch((error) => {
        if (active) {
          setState({
            sections: STATIC_MENU_SECTIONS,
            source: "static",
            loading: false,
            error: error.message || "Unable to load menu."
          });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
}

function useCateringAlgorithm() {
  const [state, setState] = useState({
    algorithm: DEFAULT_CATERING_ALGORITHM,
    source: "static",
    loading: true,
    error: ""
  });

  useEffect(() => {
    let active = true;

    loadCateringAlgorithm()
      .then((algorithm) => {
        if (active) {
          setState({
            algorithm,
            source: "database",
            loading: false,
            error: ""
          });
        }
      })
      .catch((error) => {
        if (active) {
          setState({
            algorithm: DEFAULT_CATERING_ALGORITHM,
            source: "static",
            loading: false,
            error: error.message || "Unable to load catering settings."
          });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  return state;
}

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(() => {
    if (typeof window === "undefined" || !window.matchMedia) {
      return false;
    }

    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  });

  useEffect(() => {
    if (!window.matchMedia) {
      return undefined;
    }

    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const updatePreference = () => setPrefersReducedMotion(media.matches);

    updatePreference();
    media.addEventListener?.("change", updatePreference);

    return () => {
      media.removeEventListener?.("change", updatePreference);
    };
  }, []);

  return prefersReducedMotion;
}

function useRoute() {
  const initialPath = normalizePath(window.location.pathname);
  const [path, setPath] = useState(initialPath);
  const [displayPath, setDisplayPath] = useState(initialPath);
  const [search, setSearch] = useState(() => window.location.search);
  const [transitionStage, setTransitionStage] = useState("settled");
  const timers = useRef([]);
  const prefersReducedMotion = usePrefersReducedMotion();

  const clearTimers = useCallback(() => {
    timers.current.forEach((timer) => window.clearTimeout(timer));
    timers.current = [];
  }, []);

  useEffect(() => {
    return clearTimers;
  }, [clearTimers]);

  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: prefersReducedMotion ? "auto" : "smooth" });
  }, [prefersReducedMotion]);

  const transitionTo = useCallback(
    (href, { push = false } = {}) => {
      const hrefValue = String(href || "/");
      const nextPath = normalizePath(pathnameFromHref(hrefValue));
      const nextSearch = hrefValue.includes("?") ? `?${hrefValue.split("?")[1].split("#")[0]}` : "";
      const currentPath = normalizePath(window.location.pathname);
      const currentSearch = window.location.search;

      if (push && (currentPath !== nextPath || currentSearch !== nextSearch)) {
        window.history.pushState({}, "", hrefValue);
      }

      clearTimers();

      if (prefersReducedMotion || nextPath === displayPath) {
        setPath(nextPath);
        setDisplayPath(nextPath);
        setSearch(nextSearch);
        setTransitionStage("settled");
        scrollToTop();
        return;
      }

      setPath(nextPath);
      setSearch(nextSearch);
      setTransitionStage("leaving");

      timers.current.push(
        window.setTimeout(() => {
          setDisplayPath(nextPath);
          scrollToTop();
          setTransitionStage("entering");

          timers.current.push(
            window.setTimeout(() => {
              setTransitionStage("settled");
            }, ROUTE_ENTER_MS)
          );
        }, ROUTE_EXIT_MS)
      );
    },
    [clearTimers, displayPath, prefersReducedMotion, scrollToTop]
  );

  useEffect(() => {
    const onPopState = () => transitionTo(`${window.location.pathname}${window.location.search}`);
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, [transitionTo]);

  const navigate = useCallback((href) => transitionTo(href, { push: true }), [transitionTo]);

  return { path, displayPath, search, transitionStage, navigate };
}

function Meta({ path }) {
  const menuItem = findMenuItemByPath(path, STATIC_SEARCHABLE_MENU_SECTIONS);
  const knownRoute = Boolean(menuItem || routeMeta[path] || isMenuItemPath(path));
  const meta = menuItem
    ? menuItemMeta(menuItem)
    : routeMeta[path] ||
      (knownRoute
        ? routeMeta["/"]
        : {
            title: "Page Not Found | The Gelato Spot",
            description: "The requested Gelato Spot page could not be found."
          });

  useEffect(() => {
    document.title = meta.title;

    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", meta.description);
    }

    const setMetaTag = (selector, attribute, attributeValue, content) => {
      let tag = document.querySelector(selector);
      if (!tag) {
        tag = document.createElement("meta");
        tag.setAttribute(attribute, attributeValue);
        document.head.appendChild(tag);
      }
      tag.setAttribute("content", content);
    };
    const canonicalUrl = `${site.domain}${path === "/" ? "/" : path}`;
    let canonical = document.querySelector('link[rel="canonical"]');

    if (!knownRoute) {
      canonical?.remove();
    } else {
      if (!canonical) {
        canonical = document.createElement("link");
        canonical.setAttribute("rel", "canonical");
        document.head.appendChild(canonical);
      }
      canonical.setAttribute("href", canonicalUrl);
    }

    setMetaTag('meta[name="robots"]', "name", "robots", knownRoute && !["/admin/", "/sign-in/", "/preferences/"].includes(path) ? "index,follow" : "noindex,nofollow");
    setMetaTag('meta[property="og:type"]', "property", "og:type", "website");
    setMetaTag('meta[property="og:site_name"]', "property", "og:site_name", site.name);
    setMetaTag('meta[property="og:title"]', "property", "og:title", meta.title);
    setMetaTag('meta[property="og:description"]', "property", "og:description", meta.description);
    setMetaTag('meta[property="og:url"]', "property", "og:url", knownRoute ? canonicalUrl : site.domain);
    setMetaTag('meta[property="og:image"]', "property", "og:image", absoluteSiteUrl(site.assets.hero));
    setMetaTag('meta[name="twitter:card"]', "name", "twitter:card", "summary_large_image");
    setMetaTag('meta[name="twitter:title"]', "name", "twitter:title", meta.title);
    setMetaTag('meta[name="twitter:description"]', "name", "twitter:description", meta.description);
    setMetaTag('meta[name="twitter:image"]', "name", "twitter:image", absoluteSiteUrl(site.assets.hero));
  }, [knownRoute, meta, path]);

  return null;
}

function MetaEvents({ path, search }) {
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let active = true;

    loadAppConfig()
      .then((config) => configureMetaTracking(config.meta))
      .then((enabled) => {
        if (active) setReady(enabled);
      })
      .catch(() => {
        if (active) setReady(false);
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!ready || ["/admin/", "/sign-in/", "/preferences/"].includes(path)) {
      return;
    }

    trackMetaEvent("PageView");

    const menuItem = findMenuItemByPath(path, STATIC_SEARCHABLE_MENU_SECTIONS);
    if (menuItem) {
      trackMetaEvent("ViewContent", {
        content_name: menuItem.item.name,
        content_category: menuItem.section.title,
        content_ids: [menuItemPath(menuItem.section, menuItem.item)],
        content_type: "product"
      });
    } else if (["/menu/", "/catering/", "/location/"].includes(path)) {
      trackMetaEvent("ViewContent", {
        content_name: routeMeta[path]?.title || "The Gelato Spot",
        content_category: path === "/menu/" ? "Menu" : path === "/catering/" ? "Catering" : "Location"
      });
    }

    const searchString = path === "/menu/" ? menuSearchFromRoute(search).trim() : "";
    if (searchString) {
      trackMetaEvent("Search", { search_string: searchString });
    }
  }, [path, ready, search]);

  useEffect(() => {
    if (!ready) {
      return undefined;
    }

    const trackOutboundAction = (event) => {
      const link = event.target.closest?.("a[href]");
      const href = link?.href || "";

      if (!href) return;

      const deliveryProvider = contact.delivery.find(({ href: providerUrl }) =>
        href === providerUrl || href.startsWith(providerUrl)
      );

      if (href === contact.orderUrl || href.startsWith(contact.orderUrl)) {
        trackMetaEvent("OrderOnlineClick", {
          content_name: "SkyTab online ordering",
          content_category: "Restaurant order",
          order_provider: "SkyTab",
          destination: "online_ordering"
        });
      } else if (deliveryProvider) {
        trackMetaEvent("OrderOnlineClick", {
          content_name: `${deliveryProvider.label} delivery`,
          content_category: "Third-party delivery",
          order_provider: deliveryProvider.label,
          destination: "delivery_partner"
        });
      } else if (href.startsWith("tel:") || href.startsWith("mailto:")) {
        trackMetaEvent("Contact", {
          content_name: href.startsWith("tel:") ? "Phone call" : "Email",
          contact_method: href.startsWith("tel:") ? "phone" : "email"
        });
      } else if (/maps\.google|google\.com\/maps|maps\.apple|maps\.app\.goo\.gl|waze\.com/i.test(href)) {
        trackMetaEvent("GetDirections", {
          content_name: "Gelato Spot directions",
          destination: new URL(href).hostname
        });
      }
    };

    document.addEventListener("click", trackOutboundAction, true);
    return () => document.removeEventListener("click", trackOutboundAction, true);
  }, [ready]);

  return null;
}

function AppLink({ href, navigate, className, children, onClick, ...props }) {
  const internal = href.startsWith("/");

  return (
    <a
      className={className}
      href={href}
      onClick={(event) => {
        onClick?.(event);
        if (event.defaultPrevented) {
          return;
        }
        if (internal && !event.metaKey && !event.ctrlKey && !event.shiftKey) {
          event.preventDefault();
          navigate(href);
        }
      }}
      {...(!internal ? { target: "_blank", rel: "noreferrer" } : {})}
      {...props}
    >
      {children}
    </a>
  );
}

function MotionImage({ src, alt, className = "", loading = "lazy", onLoad, onError, ...props }) {
  const [loaded, setLoaded] = useState(false);
  const [failed, setFailed] = useState(false);
  const imageRef = useRef(null);
  const classes = [
    "motion-media",
    loaded ? "is-loaded" : "is-loading",
    failed ? "has-error" : "",
    className
  ].filter(Boolean).join(" ");

  useEffect(() => {
    setLoaded(false);
    setFailed(false);

    const image = imageRef.current;
    if (image?.complete) {
      setLoaded(true);
      setFailed(!image.naturalWidth);
    }
  }, [src]);

  return (
    <span className={classes}>
      <span className="media-skeleton" aria-hidden="true" />
      <img
        ref={imageRef}
        src={src}
        alt={alt}
        loading={loading}
        decoding="async"
        onLoad={(event) => {
          setLoaded(true);
          onLoad?.(event);
        }}
        onError={(event) => {
          setLoaded(true);
          setFailed(true);
          onError?.(event);
        }}
        {...props}
      />
    </span>
  );
}

const IMAGE_POSITION_VALUES = new Set([
  "center",
  "top",
  "bottom",
  "left",
  "right",
  "top left",
  "top right",
  "bottom left",
  "bottom right"
]);

function imagePositionStyle(value) {
  const position = String(value || "").trim().toLowerCase();
  return { objectPosition: IMAGE_POSITION_VALUES.has(position) ? position : "center" };
}

function MenuImageSlot({ item, className, loading = "lazy" }) {
  if (item.imageUrl) {
    return (
      <img
        className={className}
        src={item.imageUrl}
        alt={item.imageAlt || `${item.name} at The Gelato Spot`}
        loading={loading}
        style={imagePositionStyle(item.imagePosition)}
      />
    );
  }

  return (
    <span className={`${className} menu-image-placeholder`} role="img" aria-label={`No image available for ${item.name}`}>
      No image available
    </span>
  );
}

function SecretMenuBadge({ className = "" }) {
  return (
    <span
      className={`secret-menu-badge ${className}`.trim()}
      role="img"
      aria-label="Secret menu item"
      data-tooltip="Secret menu item"
    >
      <Star size={14} aria-hidden="true" />
    </span>
  );
}

function OutOfSeasonBadge({ className = "" }) {
  return (
    <span className={`out-of-season-badge ${className}`.trim()}>
      Out of Season
    </span>
  );
}

function MenuItemAvailabilityBadge({ item, className = "" }) {
  if (isInactiveMenuItem(item)) {
    return (
      <span className={`out-of-season-badge ${className}`.trim()}>
        Inactive
      </span>
    );
  }

  if (isOutOfSeasonMenuItem(item)) {
    return <OutOfSeasonBadge className={className} />;
  }

  return null;
}

function SectionStatusBadge({ children, className = "", ...props }) {
  return (
    <span className={`section-status-badge ${className}`.trim()} {...props}>
      {children}
    </span>
  );
}

function ActionLink({ href, navigate, children, variant = "primary", icon: Icon, className = "" }) {
  return (
    <AppLink href={href} navigate={navigate} className={`button ${variant} ${className}`}>
      {Icon ? <Icon size={18} aria-hidden="true" /> : null}
      <span>{children}</span>
      {href.startsWith("/") ? <ChevronRight size={17} aria-hidden="true" /> : <ExternalLink size={16} aria-hidden="true" />}
    </AppLink>
  );
}

function Header({ path, navigate }) {
  const [open, setOpen] = useState(false);
  const [menuQuery, setMenuQuery] = useState("");

  const close = () => setOpen(false);
  const headerNavItems = navItems.filter((item) => item.label !== "FAQ");
  const submitMenuSearch = (event) => {
    event.preventDefault();
    const value = menuQuery.trim();

    navigate(value ? `/menu/?search=${encodeURIComponent(value)}` : "/menu/");
    close();
  };

  return (
    <>
      <header className="site-header">
        <AppLink href="/" navigate={navigate} className="brand" aria-label="The Gelato Spot home">
          <img src={site.assets.logoBlack} alt="" />
        </AppLink>

        <nav className="nav" aria-label="Primary navigation">
          {headerNavItems.map((item) => (
            <AppLink
              key={item.href}
              href={item.href}
              navigate={navigate}
              onClick={close}
              className={
                normalizePath(item.href) === path || (item.href === "/menu/" && path.startsWith("/menu/"))
                  ? "active"
                  : ""
              }
            >
              {item.label}
            </AppLink>
          ))}
          <a className="nav-order-link" href={contact.orderUrl} target="_blank" rel="noreferrer" onClick={close}>
            Order Online
          </a>
        </nav>

        <div className="header-actions">
          <button
            className="mobile-toggle"
            type="button"
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
            aria-controls="mobile-navigation"
            onClick={() => setOpen((value) => !value)}
          >
            {open ? <X size={22} /> : <MenuIcon size={22} />}
          </button>
        </div>
      </header>

      <nav id="mobile-navigation" className={`mobile-drawer ${open ? "open" : ""}`} aria-label="Mobile navigation">
        <form className="mobile-nav-search" role="search" onSubmit={submitMenuSearch}>
          <label htmlFor="mobile-menu-search">Search the menu</label>
          <div>
            <Search size={17} aria-hidden="true" />
            <input
              id="mobile-menu-search"
              type="search"
              value={menuQuery}
              onChange={(event) => setMenuQuery(event.target.value)}
              placeholder="Gelato, pizza, coffee..."
            />
            <button type="submit" aria-label="Search menu">
              <ArrowRight size={17} aria-hidden="true" />
            </button>
          </div>
        </form>

        <div className="mobile-nav-actions" aria-label="Quick actions">
          <a className="mobile-nav-action mobile-nav-action-primary" href={contact.orderUrl} target="_blank" rel="noreferrer" onClick={close}>
            <ShoppingBag size={17} aria-hidden="true" />
            <span>Order</span>
          </a>
          <AppLink className="mobile-nav-action" href="/location/" navigate={navigate} onClick={close}>
            <MapPin size={17} aria-hidden="true" />
            <span>Directions</span>
          </AppLink>
          {path === "/admin/" ? (
            <button
              type="button"
              className="mobile-nav-action mobile-nav-admin-action"
              onClick={() => {
                window.dispatchEvent(new Event(ADMIN_LOGOUT_EVENT));
                close();
              }}
            >
              <LogOut size={17} aria-hidden="true" />
              <span>Sign out</span>
            </button>
          ) : null}
        </div>
        {headerNavItems.map((item) => (
          <AppLink
            key={item.href}
            href={item.href}
            navigate={navigate}
            onClick={close}
            className={
              normalizePath(item.href) === path || (item.href === "/menu/" && path.startsWith("/menu/"))
                ? "active"
                : ""
            }
          >
            {item.label}
          </AppLink>
        ))}
      </nav>
    </>
  );
}

function SocialIcon({ label }) {
  const outlineIconProps = {
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: "2",
    strokeLinecap: "round",
    strokeLinejoin: "round",
    "aria-hidden": "true"
  };
  const filledIconProps = {
    viewBox: "0 0 24 24",
    fill: "currentColor",
    "aria-hidden": "true"
  };

  switch (label) {
    case "Instagram":
      return (
        <svg {...outlineIconProps}>
          <rect x="4" y="4" width="16" height="16" rx="5" />
          <circle cx="12" cy="12" r="4" />
          <path d="M17.5 6.5h.01" />
        </svg>
      );
    case "Facebook":
      return (
        <svg {...filledIconProps} viewBox="0 0 512 512">
          <path d="M504 256C504 119 393 8 256 8S8 119 8 256c0 123.8 90.7 226.4 209.3 245V327.7h-63V256h63v-54.6c0-62.2 37-96.5 93.7-96.5 27.1 0 55.5 4.8 55.5 4.8v61h-31.3c-30.8 0-40.4 19.1-40.4 38.7V256h68.8l-11 71.7h-57.8V501C413.3 482.4 504 379.8 504 256Z" />
        </svg>
      );
    case "X":
      return (
        <svg {...filledIconProps}>
          <path d="M14.4 10.2 22 2h-2.1l-6.5 7-5-7H2l8 11.2L2.4 22h2.1l6.6-7.5 5.4 7.5H22l-7.6-11.8Zm-2.3 2.6-.9-1.2L5.7 3.7h1.8l4.9 7 .9 1.2 5.9 8.4h-1.8l-5.3-7.5Z" />
        </svg>
      );
    case "Bluesky":
      return (
        <svg {...filledIconProps}>
          <path d="M12 10.9C10.9 8.7 7.9 4.8 5.2 2.9 2.6 1.1 1.6 1.4.9 1.7.1 2 0 3.2 0 3.9c0 .7.4 5.6.6 6.5.8 2.7 3.7 3.6 6.4 3.3h.4c-.1 0-.3.1-.4.1-3.9.6-7.4 2-2.8 7.1 5 5.2 6.9-1.1 7.8-4.3.9 3.2 2.8 9.5 7.8 4.3 4.6-5.1 1.1-6.5-2.8-7.1-.1 0-.3-.1-.4-.1h.4c2.7.3 5.6-.6 6.4-3.3.2-.9.6-5.8.6-6.5 0-.7-.1-1.9-.9-2.2-.7-.3-1.7-.6-4.3 1.2-2.7 1.9-5.7 5.8-6.8 8Z" />
        </svg>
      );
    case "Mastodon":
      return (
        <svg {...filledIconProps}>
          <path d="M20.9 13.6c-.3 1.8-1.7 3.3-3.5 3.7-.9.2-1.7.4-5.4.4-2.1 0-3.8-.5-3.8-.5 0 2 .9 2.7 3.8 2.8 1.7 0 3.2-.4 3.2-.4l.1 1.9s-1.2.7-3.4.8c-3.1.1-5.9-.8-6.2-4.9-.1-1.1-.1-2.3-.1-3.5V9.7c0-3.9 2.6-5 2.6-5 1.3-.6 3.5-.8 5.8-.8h.1c2.3 0 4.5.2 5.8.8 0 0 2.6 1.1 2.6 5 0 0 .1 2.9-.4 3.9h-1.2Zm-2.5-3.8c0-1-.3-1.8-.8-2.4-.5-.5-1.1-.8-2-.8-1 0-1.8.4-2.3 1.2l-.5.8-.5-.8c-.5-.8-1.3-1.2-2.3-1.2-.9 0-1.5.3-2 .8-.5.6-.8 1.4-.8 2.4v5.8h2.3V10c0-1.2.5-1.8 1.5-1.8 1.1 0 1.6.7 1.6 2.2v3.1h2.3v-3.1c0-1.5.5-2.2 1.6-2.2 1 0 1.5.6 1.5 1.8v5.6h2.3V9.8h-.1Z" />
        </svg>
      );
    default:
      return <ExternalLink size={20} aria-hidden="true" />;
  }
}

function FooterSignup({ navigate }) {
  const [form, setForm] = useState(createSubscriberState);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, siteKey: "" });

  useEffect(() => {
    let active = true;
    loadAppConfig()
      .then((config) => {
        if (active) setRecaptchaConfig(config.recaptcha || { enabled: false, siteKey: "" });
      })
      .catch(() => {
        if (active) setRecaptchaConfig({ enabled: false, siteKey: "" });
      });
    return () => {
      active = false;
    };
  }, []);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Joining the list..." });

    try {
      const query = new URLSearchParams(window.location.search);
      const metaEventId = createMetaEventId();
      const recaptchaToken =
        recaptchaConfig.enabled && recaptchaConfig.siteKey
          ? await executeRecaptcha(recaptchaConfig.siteKey, RECAPTCHA_SUBSCRIBER_ACTION)
          : "";
      const attribution = Object.fromEntries(
        ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"]
          .map((key) => [key, query.get(key) || ""])
          .filter(([, value]) => value)
      );
      const response = await fetch("/api/subscribers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          emailConsent: form.emailConsent,
          smsConsent: false,
          company: form.company,
          source: "footer_signup",
          attribution,
          metaEventId,
          recaptchaToken,
          recaptchaAction: RECAPTCHA_SUBSCRIBER_ACTION
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to save your signup.");
      }

      await trackMetaEvent(
        "Lead",
        {
          content_name: "Footer email signup",
          content_category: "Email subscriber"
        },
        { eventId: metaEventId, server: false }
      );
      setStatus({
        state: "success",
        message: "You are on the list. Watch for occasional Gelato Spot updates."
      });
      setForm(createSubscriberState());
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="footer-signup-band" aria-labelledby="footer-signup-title">
      <div className="footer-signup-inner">
        <div className="footer-signup-copy">
          <p className="eyebrow">Fresh from the neighborhood</p>
          <h2 id="footer-signup-title">Stay in the scoop.</h2>
          <p>Flavor drops, seasonal specials, and neighborhood events. Occasional emails, never daily.</p>
        </div>

        {status.state === "success" ? (
          <div className="footer-signup-success" role="status">
            <Mail size={26} aria-hidden="true" />
            <div>
              <h3>Thanks for joining us.</h3>
              <p>{status.message}</p>
            </div>
          </div>
        ) : (
          <form className="footer-signup-form" onSubmit={submit}>
            <div className="footer-signup-fields">
              <label>
                <span>First name</span>
                <input
                  value={form.name}
                  onChange={(event) => update("name", event.target.value)}
                  autoComplete="given-name"
                  required
                />
              </label>
              <label>
                <span>Email</span>
                <input
                  type="email"
                  value={form.email}
                  onChange={(event) => update("email", event.target.value)}
                  autoComplete="email"
                  inputMode="email"
                  required
                />
              </label>
              <button type="submit" className="button primary" disabled={status.state === "loading"}>
                <Mail size={18} aria-hidden="true" />
                <span>{status.state === "loading" ? "Joining..." : "Join the list"}</span>
              </button>
            </div>
            <label className="footer-signup-consent">
              <input
                type="checkbox"
                checked={form.emailConsent}
                onChange={(event) => update("emailConsent", event.target.checked)}
                required
              />
              <span>{EMAIL_SIGNUP_CONSENT}</span>
            </label>
            <label className="join-honeypot" aria-hidden="true">
              <span>Company</span>
              <input value={form.company} onChange={(event) => update("company", event.target.value)} tabIndex="-1" autoComplete="off" />
            </label>
            <p className="footer-signup-privacy">
              See our <AppLink href="/privacy-policy/" navigate={navigate}>Privacy Policy</AppLink>.
            </p>
            {status.message ? <p className={`form-status ${status.state}`} role="status">{status.message}</p> : null}
          </form>
        )}
      </div>
    </section>
  );
}

function Footer({ navigate }) {
  const footerNavItems = navItems.filter((item) => item.label !== "FAQ");
  const legalFooterLinks = [
    { label: "Join Updates", href: "/join/" },
    { label: "FAQ", href: "/frequently-asked-questions-faq/" },
    { label: "Developers", href: "/developers/" },
    { label: "Privacy Policy", href: "/privacy-policy/" },
    { label: "Terms & Conditions", href: "/terms-and-conditions/" }
  ];

  return (
    <>
      <FooterSignup navigate={navigate} />
      <footer className="site-footer">
      <div className="footer-main">
        <div className="footer-brand">
          <img src={site.assets.logoWhite} alt={site.name} className="footer-logo" />
          <ul className="footer-contact-list" aria-label="Restaurant contact information">
            <li>
              <MapPin size={16} aria-hidden="true" />
              <span>{contact.address.replace(", AZ 85028", ", AZ")}</span>
            </li>
            <li>
              <Phone size={16} aria-hidden="true" />
              <a href={contact.phoneHref}>{contact.phone}</a>
            </li>
          </ul>
        </div>
        <nav className="footer-section" aria-label="Footer primary navigation">
          <h2>Browse Our Website</h2>
          <div className="footer-link-row">
            {footerNavItems.map((item) => (
              <AppLink key={item.href} href={item.href} navigate={navigate}>
                {item.label}
              </AppLink>
            ))}
            <a href={contact.orderUrl} target="_blank" rel="noreferrer">Order Online</a>
          </div>
        </nav>
        <div className="footer-section footer-connect">
          <h2>Connect With Us</h2>
          <div className="social-list" aria-label="Social media">
            {contact.socials.map((social) => (
              <a
                key={social.href}
                className={`social-icon social-icon-${social.label.toLowerCase().replaceAll(" ", "-")}`}
                href={social.href}
                target="_blank"
                rel="noreferrer"
                aria-label={social.label}
                title={social.label}
              >
                <SocialIcon label={social.label} />
              </a>
            ))}
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <nav className="footer-legal-links" aria-label="Footer legal navigation">
          {legalFooterLinks.map((item) => (
            <AppLink key={item.href} href={item.href} navigate={navigate}>
              {item.label}
            </AppLink>
          ))}
          <a href="/sitemap.xml">Sitemap</a>
        </nav>
        <p className="copyright">
          Copyright &copy; {new Date().getFullYear()} THE GELATERIA ICE CREAM. DESSERT. LOUNGE, INC.
          <br />
          Gelato | Pizza | Coffee | Salad | Panini | Phoenix, Arizona
        </p>
      </div>
      </footer>
    </>
  );
}

function PageHero({ title, image = site.assets.hero, className = "" }) {
  return (
    <section className={`page-hero ${className}`}>
      <MotionImage src={image} alt="" className="page-hero-media" loading="eager" />
      <span className="page-hero-overlay" aria-hidden="true" />
      <h1>{title}</h1>
    </section>
  );
}

function HomePage({ navigate }) {
  const menuData = useMenuSections();
  const quickActions = [
    { label: "Location", href: "/location/", type: "gold" },
    { label: "Menu", href: "/menu/", type: "deep-gold" },
    { label: "Order Online", href: contact.orderUrl, type: "black" },
    { label: "Call Now", href: contact.phoneHref, type: "charcoal" }
  ];

  return (
    <>
      <section className="hero">
        <MotionImage src={site.assets.hero} alt="" className="hero-media" loading="eager" />
        <div className="hero-mark">
          <img src={site.assets.logoWhite} alt={site.name} />
        </div>
      </section>

      <section className="quick-actions" aria-label="Quick actions">
        {quickActions.map((item) => (
          <AppLink
            key={item.label}
            href={item.href}
            navigate={navigate}
            className={`quick-action quick-action-${item.type}`}
          >
            {item.label}
          </AppLink>
        ))}
      </section>

      <section className="split-section home-intro">
        <div>
          <p className="eyebrow">Tasteful recipes</p>
          <h1>The Gelato Spot</h1>
          <h2>Neapolitan pizza and gelato in Phoenix</h2>
          <p>
            The Gelato Spot Wood-Fired Pizzeria is centrally located in the Phoenix metro area. Come to Shea and 32nd Street for Neapolitan-style pizza straight from our wood-fired ovens and your choice of gelato flavors for dessert.
          </p>
          <div className="button-row">
            <ActionLink href={contact.orderUrl} navigate={navigate} icon={ShoppingBag}>Order online 32nd and Shea</ActionLink>
          </div>
        </div>
        <div className="photo-grid">
          <MotionImage src={site.assets.baking} alt="Gelato Spot baking and kitchen prep" />
          <MotionImage src={site.assets.redGelato} alt="Strawberry gelato flavor" />
          <MotionImage src={site.assets.patioOne} alt="The Gelato Spot restaurant" />
        </div>
      </section>

      <section className="highlight-grid legacy-gallery" aria-label="Restaurant highlights">
        {homeHighlights.map((item) => (
          <article className="highlight-card" key={item.title}>
            <MotionImage src={item.image} alt="" />
            <div>
              <p className="eyebrow">{item.eyebrow}</p>
              <h2>{item.title}</h2>
              <p>{item.body}</p>
            </div>
          </article>
        ))}
      </section>

      <section className="photo-band">
        <MotionImage src={site.assets.counter} alt="" className="photo-band-media" />
        <div>
          <p className="eyebrow">Discover</p>
          <h2>32nd and Shea</h2>
        </div>
      </section>

      <HomeMenuPreview navigate={navigate} menuData={menuData} />

      <CateringEstimator navigate={navigate} compact />
      <Gallery />
    </>
  );
}

function HomeMenuPreview({ navigate, menuData }) {
  const allSections = menuData.sections;
  const sections = useMemo(() => visibleMenuSections(allSections), [allSections]);
  const searchableSections = useMemo(() => searchableMenuSections(allSections), [allSections]);
  const [activeSlug, setActiveSlug] = useState(() => sections[0]?.slug || slugify(sections[0]?.title || ""));
  const [query, setQuery] = useState("");
  const totalItemCount = sections.reduce((total, section) => total + section.items.length, 0);
  const activeSection =
    sections.find((section) => (section.slug || slugify(section.title)) === activeSlug) ||
    sections[0] ||
    { title: "Menu", items: [] };

  useEffect(() => {
    const nextSlug = sections[0]?.slug || slugify(sections[0]?.title || "");

    if (!sections.some((section) => (section.slug || slugify(section.title)) === activeSlug)) {
      setActiveSlug(nextSlug);
    }
  }, [activeSlug, sections]);

  const visibleItems = useMemo(() => {
    const value = query.trim().toLowerCase();
    const items = value
      ? searchableSections.flatMap((section) =>
          section.items
            .filter((item) =>
              menuItemSearchText(section, item).includes(value)
            )
            .map((item) => ({
              ...item,
              sectionTitle: section.title,
              sectionStatusLabel: sectionStatusLabel(section),
              itemPath: menuItemPath(section, item),
              isSecret: isSecretMenuItem(section, item)
            }))
        )
      : activeSection.items.map((item) => ({
          ...item,
          sectionTitle: activeSection.title,
          sectionStatusLabel: sectionStatusLabel(activeSection),
          itemPath: menuItemPath(activeSection, item)
        }));

    return items.slice(0, 7);
  }, [activeSection, query, searchableSections]);
  const activeCount = query ? visibleItems.length : activeSection.items.length;
  const panelTitle = query ? "Search results" : activeSection.title;

  return (
    <section className="home-menu-showcase" aria-labelledby="home-menu-title">
      <div className="home-menu-heading">
        <div>
          <p className="eyebrow">Menu</p>
          <h2 id="home-menu-title">Wood fired pizza, gelato, and everything in between</h2>
          <p>
            Search the menu, switch sections, and jump into the full menu when something catches your eye.
          </p>
        </div>
        <div className="home-menu-actions">
          <ActionLink href="/menu/" navigate={navigate} variant="outline" icon={ShoppingBag}>Full menu</ActionLink>
          <ActionLink href={contact.orderUrl} navigate={navigate} icon={ShoppingBag}>Order online</ActionLink>
        </div>
      </div>

      <div className="home-menu-shell">
        <aside className="home-menu-controls" aria-label="Menu controls">
          <label className="home-menu-search">
            <span>Search menu</span>
            <div>
              <Search size={18} aria-hidden="true" />
              <input
                type="search"
                value={query}
                onChange={(event) => setQuery(event.target.value)}
                placeholder="Pizza, gelato, coffee..."
              />
              {query ? (
                <button type="button" onClick={() => setQuery("")} aria-label="Clear menu search">
                  <X size={16} aria-hidden="true" />
                </button>
              ) : null}
            </div>
          </label>

          <div className="home-menu-tabs" role="tablist" aria-label="Menu sections">
            {sections.map((section) => {
              const slug = section.slug || slugify(section.title);
              const active = !query && slug === (activeSection.slug || slugify(activeSection.title));
              const statusLabel = sectionStatusLabel(section);

              return (
                <button
                  key={section.title}
                  type="button"
                  role="tab"
                  aria-selected={active}
                  aria-label={statusLabel ? `${section.title}, ${statusLabel}, ${section.items.length} items` : `${section.title}, ${section.items.length} items`}
                  className={active ? "active" : ""}
                  onClick={() => {
                    setQuery("");
                    setActiveSlug(slug);
                  }}
                >
                  <span>
                    {section.title}
                    {statusLabel ? (
                      <SectionStatusBadge className="section-status-badge-tab" title={statusLabel}>
                        Temp. down
                      </SectionStatusBadge>
                    ) : null}
                  </span>
                  <strong>{section.items.length}</strong>
                </button>
              );
            })}
          </div>
        </aside>

        <div className="home-menu-panel">
          <div className="home-menu-panel-head">
            <div>
              <span>{menuData.loading ? "Loading menu" : `${totalItemCount} items`}</span>
              <h3>
                {panelTitle}
                {!query && sectionStatusLabel(activeSection) ? (
                  <SectionStatusBadge className="section-status-badge-inline">{sectionStatusLabel(activeSection)}</SectionStatusBadge>
                ) : null}
              </h3>
            </div>
            <p>{activeCount ? `${activeCount} showing` : "No matches"}</p>
          </div>

          {visibleItems.length ? (
            <ul className="home-menu-list">
              {visibleItems.map((item) => {
                const secret = Boolean(item.isSecret);
                const unavailable = isUnavailableMenuItem(item);

                return (
                  <li className={item.imageUrl ? "has-image" : ""} key={`${item.sectionTitle}-${item.name}`}>
                    <AppLink
                      href={item.itemPath}
                      navigate={navigate}
                      className={`home-menu-card${item.imageUrl ? " home-menu-card-with-media has-image" : ""}${secret ? " home-menu-card-secret" : ""}${unavailable ? " home-menu-card-out-of-season" : ""}`}
                      aria-label={`View ${item.name} details${secret ? ", secret menu item" : ""}${isInactiveMenuItem(item) ? ", inactive" : ""}${isOutOfSeasonMenuItem(item) ? ", out of season" : ""}`}
                    >
                      {item.imageUrl ? <MenuImageSlot item={item} className="home-menu-item-image" /> : null}
                      <div>
                        {query ? (
                          <span className="home-menu-item-section">
                            {item.sectionTitle}
                            {item.sectionStatusLabel ? (
                              <SectionStatusBadge className="section-status-badge-mini">{item.sectionStatusLabel}</SectionStatusBadge>
                            ) : null}
                          </span>
                        ) : null}
                        <strong className="home-menu-item-title">
                          <span>{item.name}</span>
                          {secret ? <SecretMenuBadge /> : null}
                          <MenuItemAvailabilityBadge item={item} />
                        </strong>
                        {item.description ? <p>{item.description}</p> : null}
                      </div>
                      {item.price ? <em>{item.price}</em> : null}
                    </AppLink>
                  </li>
                );
              })}
            </ul>
          ) : (
            <div className="home-menu-empty">
              <p>No menu matches yet. Try pizza, gelato, coffee, or happy hour.</p>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}

function Gallery() {
  const images = [
    site.assets.gelatoCase,
    site.assets.baking,
    site.assets.cone,
    site.assets.panini,
    site.assets.cateringTable,
    site.assets.cakes
  ];

  return (
    <section className="gallery-strip" aria-label="Gelato Spot food and catering photos">
      {images.map((image) => (
        <MotionImage key={image} src={image} alt="" />
      ))}
    </section>
  );
}

function menuChapterForSection(section) {
  return MENU_SECTION_CHAPTERS[sectionSlug(section)] || null;
}

function MenuChapterHeader({ chapter }) {
  return (
    <section className={`menu-chapter menu-chapter-${chapter.tone}`} aria-label={`${chapter.title} section`}>
      <MotionImage src={chapter.image} alt="" className="menu-chapter-media" />
      <span className="menu-chapter-overlay" aria-hidden="true" />
      <div>
        <p>{chapter.eyebrow}</p>
        <h2>{chapter.title}</h2>
      </div>
    </section>
  );
}

function isGelatoMenuSection(section) {
  return sectionSlug(section) === "gelato";
}

function isHappyHourMenuSection(section) {
  return sectionSlug(section) === "happy-hour";
}

function isGelatoFlavorItem(section, item) {
  return isGelatoMenuSection(section) && !item.price;
}

function gelatoItemGroups(section, { includeOutOfSeasonFlavors = true } = {}) {
  return (section.items || []).reduce(
    (groups, item) => {
      if (isGelatoFlavorItem(section, item)) {
        if (includeOutOfSeasonFlavors || !isOutOfSeasonMenuItem(item)) {
          groups.flavors.push(item);
        }
      } else {
        groups.prices.push(item);
      }

      return groups;
    },
    { prices: [], flavors: [] }
  );
}

function happyHourItemGroup(item) {
  const tags = (item.tags || []).map((tag) => String(tag).toLowerCase());

  if (tags.some((tag) => ["drink", "drinks", "beverage", "beer", "wine", "cocktail"].includes(tag))) {
    return "Drinks";
  }

  if (tags.some((tag) => ["food", "pizza", "salad", "appetizer", "pasta"].includes(tag))) {
    return "Food";
  }

  const name = String(item.name || "").toLowerCase();

  return HAPPY_HOUR_DRINK_MATCHERS.some((matcher) => name.includes(matcher)) ? "Drinks" : "Food";
}

function happyHourItemGroups(section) {
  return (section.items || []).reduce(
    (groups, item) => {
      const group = happyHourItemGroup(item);
      groups[group].push(item);
      return groups;
    },
    { Food: [], Drinks: [] }
  );
}

function MenuItemLink({ section, item, navigate, className = "", showSectionStatus = false }) {
  const secret = isSecretMenuItem(section, item);
  const unavailable = isUnavailableMenuItem(item);
  const statusLabel = showSectionStatus ? sectionStatusLabel(section) : "";

  return (
    <AppLink
      className={`menu-item${item.price ? " menu-item-priced" : ""}${item.imageUrl ? " menu-item-with-media" : ""}${secret ? " menu-item-secret" : ""}${unavailable ? " menu-item-out-of-season" : ""} ${className}`.trim()}
      href={menuItemPath(section, item)}
      navigate={navigate}
      aria-label={`View ${item.name} details${secret ? ", secret menu item" : ""}${isInactiveMenuItem(item) ? ", inactive" : ""}${isOutOfSeasonMenuItem(item) ? ", out of season" : ""}`}
    >
      {item.imageUrl ? <MenuImageSlot item={item} className="menu-item-image" /> : null}
      <div>
        <h3 className="menu-item-title">
          <span>{item.name}</span>
          {secret ? <SecretMenuBadge /> : null}
          <MenuItemAvailabilityBadge item={item} />
          {statusLabel ? <SectionStatusBadge className="section-status-badge-mini">{statusLabel}</SectionStatusBadge> : null}
        </h3>
        {item.description ? <p>{item.description}</p> : null}
      </div>
      {item.price ? <strong>{item.price}</strong> : null}
    </AppLink>
  );
}

function MenuItemGroup({ title, eyebrow, section, items, navigate }) {
  if (!items.length) {
    return null;
  }

  return (
    <div className="menu-item-group">
      <div className="menu-item-group-head">
        <div>
          <p>{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <span>{items.length} items</span>
      </div>
      <div className="menu-items">
        {items.map((item) => (
          <MenuItemLink section={section} item={item} navigate={navigate} key={`${section.title}-${title}-${item.name}`} />
        ))}
      </div>
    </div>
  );
}

function MenuItemContextPlacements({ placements }) {
  if (!placements.length) {
    return null;
  }

  return (
    <div className="menu-item-context-placements" aria-label="Special menu availability">
      <div className="menu-item-context-list">
        {placements.map((placement) => {
          const note = [placement.section.note, placement.item.contextDescription]
            .filter(Boolean)
            .join(" ");

          return (
            <a
              href={`/menu/#menu-section-${placement.sectionSlug}`}
              className="menu-item-context-card"
              key={`${placement.sectionSlug}-${placement.item.id || placement.itemSlug}`}
            >
              <span>{placement.section.title}</span>
              {placement.item.price ? <strong>{placement.item.price}</strong> : null}
              {note ? <p>{note}</p> : null}
            </a>
          );
        })}
      </div>
    </div>
  );
}

function PizzaPriceOptions({ entry, placements }) {
  if (!hasPizzaRangePrice(entry.section, entry.item)) {
    return null;
  }

  const happyHourPlacements = placements.filter((placement) => placement.sectionSlug === "happy-hour");

  return (
    <div className="menu-item-price-options" aria-label="Pizza pricing">
      <div className="menu-item-price-option">
        <div>
          <span>10&quot; Lunch</span>
          <p>Available until 5PM</p>
        </div>
        <strong>$13</strong>
      </div>
      <div className="menu-item-price-option">
        <div>
          <span>12&quot; Dinner</span>
          <p>Available all night</p>
        </div>
        <strong>$15</strong>
      </div>
      {happyHourPlacements.map((placement) => (
        <a
          href={`/menu/#menu-section-${placement.sectionSlug}`}
          className="menu-item-price-option menu-item-price-option-special"
          key={`${placement.sectionSlug}-${placement.item.id || placement.itemSlug}`}
        >
          <div>
            <span>Happy Hour</span>
            <p>10&quot; pizza, Tuesday through Friday, 3PM to 6PM</p>
          </div>
          {placement.item.price ? <strong>{placement.item.price}</strong> : null}
        </a>
      ))}
    </div>
  );
}

function MenuItemDetailMedia({ entry, isWaitingForImageData, className = "", loading = "lazy" }) {
  const mediaClassName = `menu-item-detail-media ${className}`.trim();

  if (entry.item.imageUrl) {
    return (
      <MotionImage
        src={entry.item.imageUrl}
        alt={entry.item.imageAlt || `${entry.item.name} at The Gelato Spot`}
        className={mediaClassName}
        loading={loading}
        style={imagePositionStyle(entry.item.imagePosition)}
      />
    );
  }

  if (isWaitingForImageData) {
    return (
      <div className={`${mediaClassName} menu-item-detail-media-skeleton`} aria-hidden="true">
        <span className="media-skeleton" aria-hidden="true" />
      </div>
    );
  }

  return (
    <div
      className={`${mediaClassName} menu-image-placeholder menu-image-placeholder-large`}
      role="img"
      aria-label={`No image available for ${entry.item.name}`}
    >
      No image available
    </div>
  );
}

function GelatoFlavorLink({ section, item, navigate }) {
  const secret = isSecretMenuItem(section, item);
  const unavailable = isUnavailableMenuItem(item);

  return (
    <AppLink
      className={`gelato-flavor${item.imageUrl ? " gelato-flavor-with-image" : ""}${secret ? " menu-item-secret" : ""}${unavailable ? " menu-item-out-of-season" : ""}`}
      href={menuItemPath(section, item)}
      navigate={navigate}
      aria-label={`View ${item.name} details${secret ? ", secret menu item" : ""}${isInactiveMenuItem(item) ? ", inactive" : ""}${isOutOfSeasonMenuItem(item) ? ", out of season" : ""}`}
    >
      {item.imageUrl ? (
        <MenuImageSlot item={item} className="gelato-flavor-image" />
      ) : (
        <span className="gelato-flavor-dot" aria-hidden="true" />
      )}
      <span className="gelato-flavor-copy">
        <strong>
          <span>{item.name}</span>
          {secret ? <SecretMenuBadge /> : null}
          <MenuItemAvailabilityBadge item={item} />
        </strong>
        {item.description ? <small>{item.description}</small> : null}
      </span>
    </AppLink>
  );
}

function GelatoSectionContent({ section, navigate }) {
  const [showOutOfSeasonFlavors, setShowOutOfSeasonFlavors] = useState(false);
  const { prices, flavors } = gelatoItemGroups(section, {
    includeOutOfSeasonFlavors: showOutOfSeasonFlavors
  });
  const outOfSeasonFlavorCount = (section.items || []).filter((item) =>
    isGelatoFlavorItem(section, item) && isOutOfSeasonMenuItem(item)
  ).length;

  return (
    <div className="gelato-menu-board">
      <div className="gelato-menu-column gelato-menu-prices">
        <div className="gelato-menu-column-header">
          <p>Prices</p>
          <h3>Sizes, shakes, packs</h3>
        </div>
        <div className="menu-items">
          {prices.map((item) => (
            <MenuItemLink section={section} item={item} navigate={navigate} key={`${section.title}-${item.name}`} />
          ))}
        </div>
      </div>
      <div className="gelato-menu-column gelato-menu-flavors">
        <div className="gelato-menu-column-header gelato-menu-column-header-with-action">
          <div>
            <p>Flavors</p>
            <h3>Available in the case</h3>
          </div>
          {outOfSeasonFlavorCount ? (
            <label className="gelato-season-toggle">
              <input
                type="checkbox"
                checked={showOutOfSeasonFlavors}
                onChange={(event) => setShowOutOfSeasonFlavors(event.target.checked)}
              />
              <span>Show out-of-season</span>
            </label>
          ) : null}
        </div>
        <div className="gelato-flavor-grid">
          {flavors.map((item) => (
            <GelatoFlavorLink section={section} item={item} navigate={navigate} key={`${section.title}-${item.name}`} />
          ))}
        </div>
      </div>
    </div>
  );
}

function HappyHourSectionContent({ section, navigate }) {
  const groups = happyHourItemGroups(section);

  return (
    <div className="menu-grouped-board menu-grouped-board-happy-hour">
      <MenuItemGroup title="Food" eyebrow="Happy Hour" section={section} items={groups.Food} navigate={navigate} />
      <MenuItemGroup title="Drinks" eyebrow="Happy Hour" section={section} items={groups.Drinks} navigate={navigate} />
    </div>
  );
}

function GelatoSearchGroup({ eyebrow, title, count, children }) {
  return (
    <div className="gelato-search-group">
      <div className="gelato-search-group-head">
        <div>
          <p>{eyebrow}</p>
          <h3>{title}</h3>
        </div>
        <span>{count} {count === 1 ? "match" : "matches"}</span>
      </div>
      {children}
    </div>
  );
}

function GelatoSearchContent({ section, navigate }) {
  const { prices, flavors } = gelatoItemGroups(section);

  return (
    <div className="gelato-search-board">
      {prices.length ? (
        <GelatoSearchGroup eyebrow="Gelato" title="Ordering" count={prices.length}>
          <div className="menu-items">
            {prices.map((item) => (
              <MenuItemLink section={section} item={item} navigate={navigate} key={`${section.title}-search-price-${item.name}`} />
            ))}
          </div>
        </GelatoSearchGroup>
      ) : null}
      {flavors.length ? (
        <GelatoSearchGroup eyebrow="Case" title="Flavors" count={flavors.length}>
          <div className="gelato-flavor-grid gelato-flavor-grid-search">
            {flavors.map((item) => (
              <GelatoFlavorLink section={section} item={item} navigate={navigate} key={`${section.title}-search-flavor-${item.name}`} />
            ))}
          </div>
        </GelatoSearchGroup>
      ) : null}
    </div>
  );
}

function MenuSectionContent({ section, navigate, isSearching }) {
  if (isGelatoMenuSection(section)) {
    return isSearching
      ? <GelatoSearchContent section={section} navigate={navigate} />
      : <GelatoSectionContent section={section} navigate={navigate} />;
  }

  if (!isSearching && isHappyHourMenuSection(section)) {
    return <HappyHourSectionContent section={section} navigate={navigate} />;
  }

  return (
    <div className="menu-items">
      {section.items.map((item) => (
        <MenuItemLink
          section={section}
          item={item}
          navigate={navigate}
          showSectionStatus={isSearching}
          key={`${section.title}-${item.name}`}
        />
      ))}
    </div>
  );
}

function MenuPage({ navigate, routeSearch = "" }) {
  const { sections } = useMenuSections();
  const routeQuery = menuSearchFromRoute(routeSearch);
  const [query, setQuery] = useState(routeQuery);
  const isSearching = query.trim().length > 0;
  const visibleSections = useMemo(() => visibleMenuSections(sections), [sections]);
  const searchableSections = useMemo(() => searchableMenuSections(sections), [sections]);
  const filteredSections = useMemo(() => {
    const value = query.trim().toLowerCase();

    if (!value) {
      return visibleSections;
    }

    return searchableSections
      .map((section) => ({
        ...section,
        items: section.items.filter((item) => menuItemSearchText(section, item).includes(value))
      }))
      .filter((section) => section.items.length);
  }, [query, searchableSections, visibleSections]);

  useEffect(() => {
    setQuery(routeQuery);
  }, [routeQuery]);

  return (
    <>
      <PageHero title="Menu" image={site.assets.hero} className="menu-hero" />
      <section className="menu-command-center" aria-labelledby="menu-page-title">
        <div className="menu-command-top">
          <div className="menu-command-copy">
            <div className="menu-command-kicker">
              <p className="eyebrow">32nd and Shea</p>
            </div>
            <h1 id="menu-page-title">Pizza, gelato, panini, coffee</h1>
            <p>{isSearching ? "Showing matching items only." : "Search or jump by section."}</p>
          </div>
          <div className="menu-command-actions">
            <label className="menu-search-field">
              <span>Search menu</span>
              <div className="menu-search-shell">
                <Search size={18} aria-hidden="true" />
                <input
                  type="search"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  placeholder="Pizza, gelato, coffee..."
                />
                {query ? (
                  <button type="button" className="menu-search-clear" onClick={() => setQuery("")} aria-label="Clear menu search">
                    <X size={16} aria-hidden="true" />
                  </button>
                ) : null}
              </div>
            </label>
            <ActionLink href={contact.orderUrl} navigate={navigate} className="menu-command-order" icon={ShoppingBag}>
              Order online
            </ActionLink>
          </div>
        </div>
        {!isSearching ? (
          <nav className="menu-section-jump" aria-label="Jump to menu section">
            {visibleSections.map((section) => {
              const statusLabel = sectionStatusLabel(section);

              return (
                <a
                  key={section.title}
                  href={`#menu-section-${sectionSlug(section)}`}
                  className={statusLabel ? "has-section-status" : ""}
                  aria-label={statusLabel ? `${section.title}, ${statusLabel}` : section.title}
                >
                  <span>{section.title}</span>
                  {statusLabel ? <SectionStatusBadge className="section-status-badge-nav">Temporarily</SectionStatusBadge> : null}
                </a>
              );
            })}
          </nav>
        ) : null}
      </section>
      <section className={`menu-sections ${isSearching ? "is-searching" : ""}`} key={query || "all"}>
        {filteredSections.length ? filteredSections.map((section) => {
          const chapter = !isSearching ? menuChapterForSection(section) : null;
          const statusLabel = sectionStatusLabel(section);

          return (
            <Fragment key={section.title}>
              {chapter ? <MenuChapterHeader chapter={chapter} /> : null}
              <article
                className={`menu-section menu-section-${sectionSlug(section)}${statusLabel ? " menu-section-status-active" : ""}`}
                id={`menu-section-${sectionSlug(section)}`}
              >
                <div className="menu-section-heading">
                  <div className="menu-section-title-row">
                    <h2>{section.title}</h2>
                    {statusLabel ? <SectionStatusBadge className="section-status-badge-section">{statusLabel}</SectionStatusBadge> : null}
                  </div>
                  {section.note ? <p>{section.note}</p> : null}
                </div>
                <MenuSectionContent section={section} navigate={navigate} isSearching={isSearching} />
              </article>
            </Fragment>
          );
        }) : <MenuEmptyState query={query} />}
      </section>
    </>
  );
}

function MenuItemPage({ path, navigate }) {
  const menuData = useMenuSections();
  const searchableSections = useMemo(() => searchableMenuSections(menuData.sections), [menuData.sections]);
  const entry = useMemo(() => findMenuItemByPath(path, searchableSections), [searchableSections, path]);
  const contextPlacements = useMemo(
    () => findMenuItemContextPlacements(entry, searchableSections),
    [entry, searchableSections]
  );
  const isGelatoFlavorDetail = Boolean(entry && isGelatoFlavorItem(entry.section, entry.item));
  const relatedItems = useMemo(() => {
    if (!entry) {
      return [];
    }

    return flattenMenuItems(searchableSections)
      .filter((itemEntry) => itemEntry.sectionSlug === entry.sectionSlug && itemEntry.itemSlug !== entry.itemSlug)
      .filter((itemEntry) => !isGelatoFlavorDetail || isGelatoFlavorItem(itemEntry.section, itemEntry.item))
      .filter((itemEntry) => {
        if (isSearchOnlyMenuItem(entry.section, entry.item)) {
          return true;
        }

        return !isSearchOnlyMenuItem(itemEntry.section, itemEntry.item);
      })
      .slice(0, 4);
  }, [entry, isGelatoFlavorDetail, searchableSections]);

  useEffect(() => {
    if (!entry) {
      return;
    }

    const meta = menuItemMeta(entry);
    const image = entry.item.imageUrl || "";
    const exactPrice = exactMenuPrice(entry.item.price);
    const unavailable = Boolean(isUnavailableMenuItem(entry.item) || sectionStatusLabel(entry.section));
    document.title = meta.title;

    const description = document.querySelector('meta[name="description"]');
    if (description) {
      description.setAttribute("content", meta.description);
    }

    let schema = document.querySelector("#menu-item-schema");
    if (!schema) {
      schema = document.createElement("script");
      schema.id = "menu-item-schema";
      schema.type = "application/ld+json";
      document.head.appendChild(schema);
    }

    schema.textContent = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "MenuItem",
      name: entry.item.name,
      description: meta.description,
      image: image ? absoluteSiteUrl(image) : undefined,
      url: absoluteSiteUrl(entry.path),
      offers: exactPrice
        ? {
            "@type": "Offer",
            priceCurrency: "USD",
            price: exactPrice,
            availability: unavailable ? "https://schema.org/OutOfStock" : "https://schema.org/InStock"
          }
        : undefined,
      provider: {
        "@type": "Restaurant",
        name: site.name,
        url: site.domain,
        telephone: contact.phoneHref.replace("tel:", ""),
        servesCuisine: ["Pizza", "Gelato", "Italian"],
        priceRange: "$$",
        address: {
          "@type": "PostalAddress",
          streetAddress: "10608 North 32nd Street",
          addressLocality: "Phoenix",
          addressRegion: "AZ",
          postalCode: "85028",
          addressCountry: "US"
        }
      }
    });

    return () => {
      schema.remove();
    };
  }, [entry]);

  if (!entry && menuData.loading) {
    return (
      <section className="menu-item-detail menu-item-detail-loading">
        <div className="skeleton-stack" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <p>Loading menu item...</p>
      </section>
    );
  }

  if (!entry) {
    return <NotFound navigate={navigate} />;
  }

  const description = entry.item.description || menuItemFallbackDescription(entry);
  const secret = isSecretMenuItem(entry.section, entry.item);
  const unavailable = isUnavailableMenuItem(entry.item);
  const statusLabel = sectionStatusLabel(entry.section);
  const showPizzaPriceOptions = hasPizzaRangePrice(entry.section, entry.item);
  const isWaitingForImageData = menuData.loading && !entry.item.imageUrl;

  return (
    <>
      <section className={`menu-item-detail${unavailable ? " menu-item-detail-out-of-season" : ""}`}>
        <div className="menu-item-detail-copy">
          <nav className="menu-breadcrumb" aria-label="Breadcrumb">
            <AppLink href="/menu/" navigate={navigate}>Menu</AppLink>
            <span aria-hidden="true">/</span>
            <a href={`/menu/#menu-section-${entry.sectionSlug}`}>{entry.section.title}</a>
          </nav>
          <p className="eyebrow">{entry.section.title}</p>
          <div className="menu-item-detail-heading">
            <h1>{entry.item.name}</h1>
            {secret ? <SecretMenuBadge className="secret-menu-badge-large" /> : null}
            <MenuItemAvailabilityBadge item={entry.item} className="out-of-season-badge-large" />
            {statusLabel ? <SectionStatusBadge className="section-status-badge-large">{statusLabel}</SectionStatusBadge> : null}
          </div>
          <MenuItemDetailMedia
            entry={entry}
            isWaitingForImageData={isWaitingForImageData}
            className="menu-item-detail-media-mobile"
            loading="eager"
          />
          {showPizzaPriceOptions ? <PizzaPriceOptions entry={entry} placements={contextPlacements} /> : null}
          {!showPizzaPriceOptions && entry.item.price ? <strong className="menu-item-detail-price">{entry.item.price}</strong> : null}
          <p className="menu-item-detail-description">{description}</p>
          {!showPizzaPriceOptions ? <MenuItemContextPlacements placements={contextPlacements} /> : null}
          <div className="button-row menu-item-detail-actions">
            {!unavailable ? <ActionLink href={contact.orderUrl} navigate={navigate} icon={ShoppingBag}>Order online</ActionLink> : null}
            <ActionLink href="/location/" navigate={navigate} variant="outline" icon={MapPin}>Get directions</ActionLink>
            <ActionLink href="/menu/" navigate={navigate} variant="outline" icon={ArrowRight}>Back to menu</ActionLink>
          </div>
          <p className="menu-item-detail-service-copy">
            Visit The Gelato Spot at 32nd Street and Shea in Phoenix for Neapolitan wood-fired pizza, gelato, panini, salads, coffee, wine, and catering from a family-owned restaurant.
          </p>
        </div>
        <MenuItemDetailMedia
          entry={entry}
          isWaitingForImageData={isWaitingForImageData}
          className="menu-item-detail-media-desktop"
          loading="eager"
        />
      </section>

      <section className="menu-item-seo-panel">
        <div>
          <h2>{entry.item.name} in Phoenix</h2>
          <p>
            The Gelato Spot serves {entry.item.name} at 10608 North 32nd Street in Phoenix, near 32nd Street and Shea behind Walgreens. Menu pricing and availability can change, so online ordering or a quick call is the best way to confirm today&apos;s options.
          </p>
        </div>
        <div>
          <h2>Good to know</h2>
          <ul className="check-list">
            <li>Pickup ordering is available through SkyTab.</li>
            <li>Delivery partners may include DoorDash, Grubhub, and Uber Eats.</li>
            <li>Catering is available for gelato, wood fired pizza, panini, salads, and cakes.</li>
          </ul>
        </div>
      </section>

      {relatedItems.length ? (
        <section className="related-menu-items" aria-labelledby="related-menu-items-title">
          <div className="section-heading">
            <p className="eyebrow">{isGelatoFlavorDetail ? "More gelato flavors" : `More from ${entry.section.title}`}</p>
            <h2 id="related-menu-items-title">{isGelatoFlavorDetail ? "Related flavors" : "Related menu items"}</h2>
          </div>
          <div className="related-menu-grid">
            {relatedItems.map((itemEntry) => (
              <AppLink
                key={itemEntry.path}
                href={itemEntry.path}
                navigate={navigate}
                className="related-menu-card"
              >
                {itemEntry.item.imageUrl ? (
                  <MenuImageSlot item={itemEntry.item} className="related-menu-card-image" />
                ) : null}
                <span>{itemEntry.section.title}</span>
                <strong>{itemEntry.item.name}</strong>
                {itemEntry.item.price ? <em>{itemEntry.item.price}</em> : null}
              </AppLink>
            ))}
          </div>
        </section>
      ) : null}
    </>
  );
}

function MenuEmptyState({ query }) {
  return (
    <div className="menu-empty-state">
      <div className="skeleton-stack" aria-hidden="true">
        <span />
        <span />
        <span />
      </div>
      <h2>No menu items found</h2>
      <p>Nothing matched "{query.trim()}". Try pizza, gelato, coffee, or catering.</p>
    </div>
  );
}

function CateringEstimator({ compact = false, navigate }) {
  const { algorithm } = useCateringAlgorithm();
  const [quote, setQuote] = useState(() => createDefaultCateringQuote());
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);

  useEffect(() => {
    setQuote((current) => clampCateringQuote(current, algorithm));
  }, [algorithm]);

  const estimate = calculateCateringEstimate(quote, algorithm);
  const serviceStyle = quote.staffed ? "Staffed service" : "Self-service setup";
  const estimateSummary = [
    `${quote.people} guests`,
    `${quote.duration} ${quote.duration === 1 ? "hour" : "hours"}`,
    `${quote.flavors} flavors`,
    serviceStyle
  ];

  const update = (field, value) => {
    setQuote((current) => ({
      ...current,
      [field]: value
    }));
  };

  return (
    <section className={`catering-estimator ${compact ? "compact" : ""}`} id="catering-estimator">
      <div className="catering-estimator-header">
        <div>
          <p className="eyebrow">Catering</p>
          <h2>Bring Gelato Spot to your next event</h2>
          <p>
            Book gelato, wood fired pizza, panini, salads, and cakes for office lunches, weddings, school events, private parties, and neighborhood gatherings around Phoenix.
          </p>
        </div>
        <div className="catering-service-notes" aria-label="Catering service highlights">
          <span>Self-service gelato starts at ${algorithm.basePrice.toLocaleString()}</span>
          <span>Staffed service available</span>
          <span>Pizza, panini, salads, and cakes by request</span>
        </div>
      </div>

      <div className="estimator-layout">
        <div className="catering-visual">
          <MotionImage src={site.assets.cateringTable} alt="Gelato Spot catering table setup" />
          <div>
            <span>Popular for</span>
            <strong>Office events, birthdays, weddings, and pop-ups</strong>
          </div>
        </div>
        <div className="controls-panel">
          <div className="controls-panel-head">
            <span>Build a planning estimate</span>
            <p>Choose the basics. The team will confirm menu, timing, travel, staffing, and availability before booking.</p>
          </div>
          <RangeField
            label="Guests"
            min={algorithm.minPeople}
            max={algorithm.maxPeople}
            value={quote.people}
            suffix="guests"
            onChange={(value) => update("people", value)}
          />
          <RangeField
            label="Duration"
            min={algorithm.minHours}
            max={algorithm.maxHours}
            value={quote.duration}
            suffix="hours"
            onChange={(value) => update("duration", value)}
          />
          <RangeField
            label="Flavors"
            min={algorithm.minFlavors}
            max={algorithm.maxFlavors}
            value={quote.flavors}
            suffix="flavors"
            onChange={(value) => update("flavors", value)}
          />
          <label className="toggle-row">
            <span>
              <strong>Staffed service</strong>
              <small>Have our team serve during the event.</small>
            </span>
            <input
              type="checkbox"
              checked={quote.staffed}
              onChange={(event) => update("staffed", event.target.checked)}
            />
          </label>
        </div>
        <div className="estimate-panel">
          <span>Planning estimate</span>
          <strong>${estimate.toLocaleString()}</strong>
          <p>Based on {estimateSummary.join(", ").toLowerCase()}.</p>
          <div className="estimate-details" aria-label="Estimate details">
            {estimateSummary.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
          <p className="estimate-note">Final pricing is confirmed after the team reviews your event details.</p>
          <div className="button-row">
            <button
              type="button"
              className="button primary"
              onClick={() => {
                trackMetaEvent("CateringFormOpen", {
                  content_name: "Catering quote form",
                  content_category: "Catering"
                });
                setQuoteModalOpen(true);
              }}
            >
              <Mail size={18} aria-hidden="true" />
              <span>Request catering</span>
              <ChevronRight size={17} aria-hidden="true" />
            </button>
            <ActionLink href={contact.phoneHref} navigate={navigate} variant="outline" icon={Phone}>Call us</ActionLink>
          </div>
        </div>
      </div>
      {compact ? (
        <MotionImage src={site.assets.truck} alt="The Gelato Spot catering truck" className="catering-truck-strip" />
      ) : null}
      {quoteModalOpen ? (
        <CateringQuoteModal
          quote={quote}
          estimate={estimate}
          onClose={() => setQuoteModalOpen(false)}
        />
      ) : null}
    </section>
  );
}

function RangeField({ label, min, max, value, suffix, onChange }) {
  const progress = ((value - min) / (max - min)) * 100;

  return (
    <label className="range-field" style={{ "--range-progress": `${progress}%` }}>
      <span>
        {label}
        <strong>{value} {suffix}</strong>
      </span>
      <input
        type="range"
        min={min}
        max={max}
        value={value}
        onChange={(event) => onChange(Number(event.target.value))}
      />
    </label>
  );
}

function CateringQuoteModal({ quote, estimate, onClose }) {
  const dialogRef = useRef(null);

  useLayoutEffect(() => {
    const focusInitialField = () => {
      const focusTarget =
        dialogRef.current?.querySelector('.lead-form input:not([tabindex="-1"]), .lead-form select, .lead-form textarea') ||
        dialogRef.current?.querySelector("button, input, select, textarea") ||
        dialogRef.current;

      focusTarget?.focus?.({ preventScroll: true });
    };
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }

      if (event.key !== "Tab") {
        return;
      }

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element.offsetParent !== null);

      if (!focusable.length) {
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    const previousActiveElement = document.activeElement;

    document.addEventListener("keydown", onKeyDown);
    document.documentElement.classList.add("modal-open");
    document.body.classList.add("modal-open");
    focusInitialField();
    const focusTimer = window.setTimeout(focusInitialField, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.documentElement.classList.remove("modal-open");
      document.body.classList.remove("modal-open");
      previousActiveElement?.focus?.();
    };
  }, [onClose]);

  if (typeof document === "undefined") {
    return null;
  }

  return createPortal(
    <div
      className="modal-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) {
          onClose();
        }
      }}
    >
      <div
        ref={dialogRef}
        className="quote-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-modal-title"
        tabIndex={-1}
      >
        <button type="button" className="modal-close" onClick={onClose} aria-label="Close quote request">
          <X size={20} aria-hidden="true" />
        </button>
        <div className="quote-modal-intro">
          <p className="eyebrow">Request a quote</p>
          <h2 id="quote-modal-title">Tell us about your event</h2>
          <p>
            Share a few details and the Gelato Spot team will confirm menu, availability, timing, delivery, and final pricing.
          </p>
        </div>
        <CateringLeadForm quote={quote} estimate={estimate} embedded />
      </div>
    </div>,
    document.body
  );
}

function CateringPage({ navigate }) {
  return (
    <>
      <PageHero title="The Gelato Spot Catering" image={site.assets.cateringHero} />
      <CateringEstimator navigate={navigate} />
      <section className="split-section reverse">
        <MotionImage src={site.assets.server} alt="Gelato Spot catering service" className="catering-included-photo" />
        <div>
          <p className="eyebrow">Included</p>
          <h2>Simple, easy, and hassle free</h2>
          <ul className="check-list">
            <li>Choice of four gelato flavors for 100 to 150 people, depending on event size.</li>
            <li>Gelato Spot server for two hours, with extra time available.</li>
            <li>Complete setup and cleanup.</li>
            <li>Cups, spoons, napkins, and delivery planning.</li>
            <li>Pizza, panini, salads, and gelato cakes available by request.</li>
          </ul>
        </div>
      </section>
    </>
  );
}

function createCateringLeadFormState(quote) {
  return {
    name: "",
    email: "",
    phone: "",
    cateringType: "Gelato and wood fired pizza",
    guestCount: quote?.people ? String(quote.people) : "",
    eventAddress: "",
    company: "",
    eventNotes: ""
  };
}

function CateringLeadForm({ quote, estimate, embedded = false }) {
  const [form, setForm] = useState(() => createCateringLeadFormState(quote));
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, siteKey: "" });

  useEffect(() => {
    let active = true;

    loadAppConfig()
      .then((config) => {
        if (active) {
          setRecaptchaConfig(config.recaptcha || { enabled: false, siteKey: "" });
        }
      })
      .catch(() => {
        if (active) {
          setRecaptchaConfig({ enabled: false, siteKey: "" });
        }
      });

    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!quote?.people) {
      return;
    }

    setForm((current) => ({
      ...current,
      guestCount: current.guestCount || String(quote.people)
    }));
  }, [quote?.people]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Sending..." });

    try {
      const metaEventId = createMetaEventId();
      const recaptchaToken =
        recaptchaConfig.enabled && recaptchaConfig.siteKey
          ? await executeRecaptcha(recaptchaConfig.siteKey, RECAPTCHA_CATERING_ACTION)
          : "";

      const response = await fetch("/api/catering-leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          guestCount: form.guestCount ? Number(form.guestCount) : undefined,
          quote: quote
            ? {
                ...quote,
                estimate
              }
            : undefined,
          metaEventId,
          recaptchaToken,
          recaptchaAction: RECAPTCHA_CATERING_ACTION
        })
      });
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || "Unable to send the request.");
      }

      setStatus({
        state: "success",
        message: "Thanks. Your catering request was saved. We will follow up soon."
      });
      await trackMetaEvent(
        "Lead",
        {
          content_name: "Catering quote request",
          content_category: form.cateringType || "Catering"
        },
        { eventId: metaEventId, server: false }
      );
      setForm(createCateringLeadFormState(quote));
    } catch (error) {
      setStatus({
        state: "error",
        message: `${error.message} You can also call ${contact.phone} or email ${contact.email}.`
      });
    }
  };

  return (
    <section className={`form-section catering-lead-form-section ${embedded ? "embedded" : ""}`}>
      {!embedded ? (
        <div>
          <p className="eyebrow">Request a quote</p>
          <h2>Tell us about your event</h2>
          <p>
            Share a few details and the team will confirm menu, timing, delivery, and final pricing.
          </p>
        </div>
      ) : null}
      <form onSubmit={submit} className="lead-form">
        <label className="join-honeypot" aria-hidden="true">
          Company
          <input name="company" value={form.company} onChange={(event) => update("company", event.target.value)} tabIndex={-1} autoComplete="off" />
        </label>
        <label>
          <span>Name</span>
          <input value={form.name} onChange={(event) => update("name", event.target.value)} required />
        </label>
        <label>
          <span>Email</span>
          <input type="email" value={form.email} onChange={(event) => update("email", event.target.value)} required />
        </label>
        <label>
          <span>Phone</span>
          <input type="tel" value={form.phone} onChange={(event) => update("phone", event.target.value)} />
        </label>
        <label>
          <span>What would you like to cater?</span>
          <select value={form.cateringType} onChange={(event) => update("cateringType", event.target.value)}>
            {cateringPackages.map((item) => (
              <option key={item}>{item}</option>
            ))}
          </select>
        </label>
        <label>
          <span>For how many?</span>
          <input
            type="number"
            min="1"
            value={form.guestCount}
            onChange={(event) => update("guestCount", event.target.value)}
          />
        </label>
        <label>
          <span>Catering address</span>
          <input value={form.eventAddress} onChange={(event) => update("eventAddress", event.target.value)} />
        </label>
        <label className="full">
          <span>More details</span>
          <textarea value={form.eventNotes} onChange={(event) => update("eventNotes", event.target.value)} rows="5" />
        </label>
        <button className="button primary" type="submit" disabled={status.state === "loading"}>
          <Mail size={18} aria-hidden="true" />
          <span>{status.state === "loading" ? "Sending" : "Send request"}</span>
        </button>
        {status.message ? <p className={`form-status ${status.state}`}>{status.message}</p> : null}
      </form>
    </section>
  );
}

function LocationTools({ children = null }) {
  const [openStatus, setOpenStatus] = useState(() => getOpenStatus());

  useEffect(() => {
    const interval = window.setInterval(() => {
      setOpenStatus(getOpenStatus());
    }, 60000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <div className="location-tools">
      <div className={`open-status open-status-${openStatus.state}`}>
        <Clock size={18} aria-hidden="true" />
        <div>
          <strong>{openStatus.label}</strong>
          <span>{openStatus.detail}</span>
        </div>
      </div>

      {children}
      <WeeklyHours />
    </div>
  );
}

function LocationMapActions() {
  const [copyStatus, setCopyStatus] = useState("");
  const [distanceStatus, setDistanceStatus] = useState({ state: "idle", message: "" });
  const [showAppleMaps, setShowAppleMaps] = useState(false);
  const encodedAddress = encodeURIComponent(contact.address);
  const googleDirectionsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodedAddress}`;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodedAddress}`;
  const appleMapsUrl = `https://maps.apple.com/?daddr=${encodedAddress}&dirflg=d`;
  const wazeUrl = `https://waze.com/ul?q=${encodedAddress}&navigate=yes`;

  useEffect(() => {
    setShowAppleMaps(isAppleDevice());
  }, []);

  const copyAddress = async () => {
    try {
      await navigator.clipboard.writeText(contact.address);
      setCopyStatus("Address copied");
    } catch (_error) {
      setCopyStatus("Copy failed. The address is listed above.");
    }
  };

  const estimateDistance = () => {
    if (!navigator.geolocation) {
      setDistanceStatus({
        state: "error",
        message: "Location sharing is not available in this browser."
      });
      return;
    }

    setDistanceStatus({ state: "loading", message: "Checking your distance..." });
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const miles = distanceInMiles(
          {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          },
          LOCATION_COORDS
        );

        setDistanceStatus({
          state: "success",
          message: `About ${miles < 10 ? miles.toFixed(1) : Math.round(miles)} miles away`
        });
      },
      () => {
        setDistanceStatus({
          state: "error",
          message: "Location permission was not granted."
        });
      },
      {
        enableHighAccuracy: false,
        maximumAge: 300000,
        timeout: 10000
      }
    );
  };

  return (
    <>
      <div className="location-map-actions" aria-label="Map and directions options">
        <a className="button primary" href={googleDirectionsUrl} target="_blank" rel="noreferrer">
          <Navigation size={18} aria-hidden="true" />
          <span>Get directions</span>
        </a>
        <a className="button outline" href={contact.phoneHref}>
          <Phone size={18} aria-hidden="true" />
          <span>Call</span>
        </a>
        {showAppleMaps ? (
          <a className="button outline" href={appleMapsUrl} target="_blank" rel="noreferrer">
            <MapPin size={18} aria-hidden="true" />
            <span>Apple Maps</span>
          </a>
        ) : null}
        <a className="button outline location-google-map-action" href={googleMapsUrl} target="_blank" rel="noreferrer">
          <MapPin size={18} aria-hidden="true" />
          <span>Google Maps</span>
        </a>
        <a className="button outline" href={wazeUrl} target="_blank" rel="noreferrer">
          <Navigation size={18} aria-hidden="true" />
          <span>Waze</span>
        </a>
        <button className="button outline" type="button" onClick={copyAddress}>
          <Copy size={18} aria-hidden="true" />
          <span>Copy address</span>
        </button>
        <button
          className="button outline"
          type="button"
          onClick={estimateDistance}
          disabled={distanceStatus.state === "loading"}
        >
          <LocateFixed size={18} aria-hidden="true" />
          <span>{distanceStatus.state === "loading" ? "Checking" : "Distance from me"}</span>
        </button>
      </div>

      {copyStatus || distanceStatus.message ? (
        <p className={`location-tool-status ${distanceStatus.state === "error" ? "error" : ""}`} aria-live="polite">
          {[copyStatus, distanceStatus.message].filter(Boolean).join(" ")}
        </p>
      ) : null}
    </>
  );
}

function formatHoursLabel(hours) {
  if (hours.closed) {
    return "Closed";
  }

  return `${formatMinutes(minutesFromTime(hours.open))} - ${formatMinutes(minutesFromTime(hours.close))}`;
}

function WeeklyHours() {
  const [currentDayIndex, setCurrentDayIndex] = useState(() => getPhoenixDayAndMinutes().dayIndex);

  useEffect(() => {
    const interval = window.setInterval(() => {
      setCurrentDayIndex(getPhoenixDayAndMinutes().dayIndex);
    }, 60000);

    return () => window.clearInterval(interval);
  }, []);

  return (
    <section className="weekly-hours-card" aria-labelledby="weekly-hours-title">
      <div className="weekly-hours-heading">
        <Clock size={18} aria-hidden="true" />
        <h3 id="weekly-hours-title">Hours</h3>
      </div>
      <ul className="weekly-hours-list">
        {LOCATION_HOURS.map((hours, index) => (
          <li className={index === currentDayIndex ? "is-today" : ""} key={hours.day}>
            <span>{hours.day}</span>
            <strong>{formatHoursLabel(hours)}</strong>
            {index === currentDayIndex ? <em>Today</em> : null}
          </li>
        ))}
      </ul>
    </section>
  );
}

function LocationPage({ navigate }) {
  return (
    <>
      <section className="location-headline">
        <p className="eyebrow">Visit us</p>
        <h1>32nd Street & Shea | NW Corner (behind Walgreens)</h1>
        <p>Catering / Pop-ups / Deliveries (large orders) across the valley!</p>
      </section>
      <section className="location-layout">
        <div className="location-details">
          <h2>Plan your stop</h2>
          <div className="location-address-block">
            <MapPin size={20} aria-hidden="true" />
            <div>
              <strong>{contact.address}</strong>
              <span>{contact.shortAddress}</span>
            </div>
          </div>
          <LocationTools>
            <div className="location-actions-mobile">
              <LocationMapActions />
            </div>
          </LocationTools>
          <div className="info-list">
            <a href={contact.phoneHref}><Phone size={18} /> {contact.phone}</a>
            <a href={contact.emailHref}><Mail size={18} /> {contact.email}</a>
          </div>
          <div className="button-row">
            <ActionLink href={contact.orderUrl} navigate={navigate} icon={ShoppingBag}>Order online</ActionLink>
            <ActionLink href="/catering/" navigate={navigate} variant="outline" icon={Mail}>Book catering</ActionLink>
          </div>
        </div>
        <div className="location-map-panel">
          <iframe
            title="Map to The Gelato Spot"
            src={contact.mapEmbed}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
          <div className="location-actions-desktop">
            <LocationMapActions />
          </div>
        </div>
      </section>
    </>
  );
}

function AboutPage() {
  return (
    <>
      <PageHero title="About The Gelato Spot" image={site.assets.hero} />
      <section className="story-section">
        <div>
          <h2>From gelato to a full Italian pizzeria</h2>
          <p>
            There are many words you could use to describe what we do, but one is more important to us than any other: Authentic. It all started with gelato, which meant going straight to the original source, so that's exactly what we did. We went to Northern Italy and met with gelato masters who had been developing their craft for generations, some for more than 200 years. We built relationships and partnerships that gave us the ability to offer some of the finest and oldest gelato ingredients available in Italy.
          </p>
          <p>
            When we brought that knowledge home, we empowered the classical North Italian techniques with modern technology to make gelato that was consistently perfect in every batch. If we don't import it directly from Italy, we make it ourselves to ensure that we offer a level of quality that can't be matched by anyone else, anywhere in the United States.
          </p>
          <p>
            Gelato was just the beginning. We've brought the philosophy that made our company a success to our coffee, pizzas, cakes, juice and other fine foods. We still focus on making food based on strict old-world standards with the freshest ingredients and most-exclusive imports. We hope that you'll come in to experience our food today. It would be our pleasure to show you what our dedication means to your dining experience.
          </p>
        </div>
        <div className="about-photo-collage" aria-label="Gelato Spot food and restaurant photos">
          <MotionImage src={site.assets.counter} alt="Gelato Spot counter" className="about-photo about-photo-primary" />
          <MotionImage src={site.assets.redGelato} alt="Fresh red gelato" className="about-photo" />
          <MotionImage src={site.assets.pizza} alt="Wood fired pizza from The Gelato Spot" className="about-photo" />
        </div>
      </section>
    </>
  );
}

function FAQPage({ navigate }) {
  return (
    <>
      <PageHero title="Frequently Asked Questions" image={site.assets.hero} />
      <section className="faq-list">
        {faqs.map((faq, index) => (
          <details key={faq.question} open={index === 0}>
            <summary>{faq.question}</summary>
            <p>{faq.answer}</p>
          </details>
        ))}
      </section>
      <section className="delivery-section">
        <div>
          <p className="eyebrow">Delivery</p>
          <h2>Order direct or through delivery partners</h2>
          <p>Choose pickup through SkyTab or delivery from your preferred app.</p>
        </div>
        <div className="delivery-links">
          <ActionLink href={contact.orderUrl} navigate={navigate} icon={ShoppingBag}>Order pickup</ActionLink>
          {contact.delivery.map((item) => (
            <ActionLink key={item.href} href={item.href} navigate={navigate} variant="outline" icon={ExternalLink}>
              {item.label}
            </ActionLink>
          ))}
        </div>
      </section>
    </>
  );
}

function LegalPage({ slug }) {
  const page = legalPages[slug];

  return (
    <section className="legal-page">
      <p className="eyebrow">Legal</p>
      <h1>{page.title}</h1>
      <p className="effective-date">Effective date: {page.effectiveDate}</p>
      {page.paragraphs.map((paragraph) => (
        <p key={paragraph}>{paragraph}</p>
      ))}
    </section>
  );
}

function DevelopersPage({ navigate }) {
  const restEndpoints = [
    ["Business information", "/api/v1/business"],
    ["Current menu", "/api/v1/menu"],
    ["Current flavors", "/api/v1/flavors"],
    ["Frequently asked questions", "/api/v1/faqs"],
    ["Catering estimate", "/api/v1/catering/estimate?people=50&durationHours=3&staffed=true&flavors=4"]
  ];

  return (
    <div className="developers-page">
      <section className="developers-hero">
        <p className="eyebrow">Developers and AI assistants</p>
        <h1>Build with The Gelato Spot.</h1>
        <p className="developers-lede">
          Use a free, read-only REST API or MCP server to retrieve public restaurant information,
          explore the current menu and flavors, estimate catering, and hand customers back to the
          official ordering or catering experience.
        </p>
        <div className="button-row">
          <a className="button primary" href="/openapi.json">
            <span>OpenAPI document</span>
            <ExternalLink size={18} aria-hidden="true" />
          </a>
          <a className="button outline" href="/.well-known/mcp.json">
            <span>MCP metadata</span>
            <ExternalLink size={18} aria-hidden="true" />
          </a>
        </div>
      </section>

      <section className="developers-grid" aria-label="Developer interfaces">
        <article className="developer-card developer-card-featured">
          <p className="eyebrow">MCP server</p>
          <h2>Connect an AI assistant</h2>
          <p>
            Add the Streamable HTTP endpoint to an MCP-compatible client. The public tools are
            anonymous, read-only, and limited to customer-facing restaurant information.
          </p>
          <pre><code>https://gelato.spot/mcp</code></pre>
          <dl className="developer-facts">
            <div><dt>Transport</dt><dd>Streamable HTTP</dd></div>
            <div><dt>Authentication</dt><dd>None for public tools</dd></div>
            <div><dt>Mutations</dt><dd>Not supported</dd></div>
          </dl>
        </article>

        <article className="developer-card">
          <p className="eyebrow">REST API</p>
          <h2>Use stable JSON endpoints</h2>
          <p>
            Version 1 provides cacheable JSON for public business details, menu items, flavors,
            FAQs, and non-binding catering estimates.
          </p>
          <pre><code>GET https://gelato.spot/api/v1/menu</code></pre>
          <a className="developer-text-link" href="/api/v1">
            Explore API discovery <ArrowRight size={16} aria-hidden="true" />
          </a>
        </article>

        <article className="developer-card">
          <p className="eyebrow">AI discovery</p>
          <h2>Machine-readable context</h2>
          <p>
            Use the OpenAPI contract, MCP metadata, or concise LLM guide to discover supported
            capabilities without scraping the customer website.
          </p>
          <ul className="developer-resource-list">
            <li><a href="/openapi.json">OpenAPI 3.1</a></li>
            <li><a href="/.well-known/mcp.json">MCP server metadata</a></li>
            <li><a href="/llms.txt">llms.txt</a></li>
          </ul>
        </article>

        <article className="developer-card developer-card-safety">
          <p className="eyebrow">Safety boundary</p>
          <h2>Customers complete every action</h2>
          <p>
            These interfaces cannot place orders, charge cards, create reservations, submit forms,
            or access customer, subscriber, analytics, inbox, or admin data. Menu prices, flavors,
            availability, and estimates can change.
          </p>
          <AppLink className="developer-text-link" href="/catering/" navigate={navigate}>
            Continue to catering <ArrowRight size={16} aria-hidden="true" />
          </AppLink>
        </article>
      </section>

      <section className="developer-endpoints" aria-labelledby="developer-endpoints-title">
        <div>
          <p className="eyebrow">REST API v1</p>
          <h2 id="developer-endpoints-title">Public endpoints</h2>
          <p>
            Requests are read-only, require no API key, and support cross-origin GET requests.
            Responses identify the contract version and exclude secret, inactive, and out-of-season content.
          </p>
        </div>
        <div className="developer-endpoint-list">
          {restEndpoints.map(([label, href]) => (
            <a key={href} href={href}>
              <span>{label}</span>
              <code>GET {href}</code>
            </a>
          ))}
        </div>
      </section>

      <section className="developer-contact-band">
        <div>
          <p className="eyebrow">Questions or responsible disclosure</p>
          <h2>Talk with the Gelato Spot team.</h2>
        </div>
        <a className="button primary" href={contact.emailHref}>Email {contact.email}</a>
      </section>
    </div>
  );
}

function PreferencesPage({ routeSearch }) {
  const token = useMemo(() => new URLSearchParams(routeSearch).get("token") || "", [routeSearch]);
  const [preferences, setPreferences] = useState(null);
  const [subscribed, setSubscribed] = useState(false);
  const [status, setStatus] = useState({ state: token ? "loading" : "error", message: token ? "Loading your preferences..." : "Open the secure preferences link from a Gelato Spot email." });

  useEffect(() => {
    let active = true;
    if (!token) return undefined;

    setStatus({ state: "loading", message: "Loading your preferences..." });
    fetch(`/api/marketing/preferences?token=${encodeURIComponent(token)}`)
      .then(async (response) => {
        const payload = await response.json();
        if (!response.ok) throw new Error(payload.message || "Unable to load your preferences.");
        return payload.preferences;
      })
      .then((nextPreferences) => {
        if (!active) return;
        setPreferences(nextPreferences);
        setSubscribed(nextPreferences.subscribed === true);
        setStatus({ state: "ready", message: "" });
      })
      .catch((error) => {
        if (active) setStatus({ state: "error", message: error.message });
      });

    return () => {
      active = false;
    };
  }, [token]);

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Saving your preferences..." });

    try {
      const response = await fetch("/api/marketing/preferences", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, subscribed })
      });
      const payload = await response.json();
      if (!response.ok) throw new Error(payload.message || "Unable to save your preferences.");
      setPreferences(payload.preferences);
      setSubscribed(payload.preferences.subscribed === true);
      setStatus({
        state: "success",
        message: payload.preferences.subscribed
          ? "You will continue receiving occasional Gelato Spot emails."
          : "You have been unsubscribed from Gelato Spot promotional emails."
      });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="preferences-page">
      <div className="preferences-intro">
        <p className="eyebrow">Email preferences</p>
        <h1>You are in control of your inbox.</h1>
        <p>Choose whether you want occasional news, specials, flavors, and events from The Gelato Spot.</p>
      </div>
      <div className="preferences-card">
        <div className="preferences-icon" aria-hidden="true"><Mail size={28} /></div>
        <div>
          <p className="preferences-kicker">The Gelato Spot updates</p>
          <h2>Manage promotional emails</h2>
          {preferences ? <p className="preferences-email">For {preferences.maskedEmail}</p> : null}
        </div>
        {preferences ? (
          <form onSubmit={submit} className="preferences-form">
            <label className="preferences-choice">
              <input
                type="checkbox"
                checked={subscribed}
                onChange={(event) => setSubscribed(event.target.checked)}
                disabled={status.state === "loading"}
              />
              <span>
                <strong>Receive Gelato Spot updates</strong>
                <small>Occasional pizza, gelato, special, and event emails. No daily clutter.</small>
              </span>
            </label>
            <button className="button primary" type="submit" disabled={status.state === "loading"}>
              {status.state === "loading" ? "Saving..." : "Save preferences"}
            </button>
          </form>
        ) : null}
        {status.message ? <p className={`form-status ${status.state}`} role="status">{status.message}</p> : null}
        <p className="preferences-help">Need help? Email <a href={contact.emailHref}>{contact.email}</a> or call <a href={contact.phoneHref}>{contact.phone}</a>.</p>
      </div>
    </section>
  );
}

function SignInPage() {
  const [form, setForm] = useState({ email: "", password: "" });
  const [status, setStatus] = useState({ state: "idle", message: "" });

  const update = (field, value) => setForm((current) => ({ ...current, [field]: value }));

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Signing in..." });

    try {
      const response = await fetch("/api/auth/login", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const payload = await response.json();

      if (!response.ok) {
        throw new Error(payload.message || "Unable to sign in.");
      }

      setStatus({ state: "success", message: `Signed in as ${payload.user.email || payload.user.name}.` });
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="sign-in-page">
      <div>
        <p className="eyebrow">Staff</p>
        <h1>Shared Manna sign-in</h1>
        <p>
          This form is wired to the shared Postgres user table candidates. Once Railway has `DATABASE_URL` and `SESSION_SECRET`, staff can sign in through the API.
        </p>
      </div>
      <form onSubmit={submit} className="sign-in-form">
        <User size={28} aria-hidden="true" />
        <label>
          <span>Email</span>
          <input
            type="email"
            value={form.email}
            onChange={(event) => update("email", event.target.value)}
            autoComplete="email"
            required
          />
        </label>
        <label>
          <span>Password</span>
          <input
            type="password"
            value={form.password}
            onChange={(event) => update("password", event.target.value)}
            autoComplete="current-password"
            required
          />
        </label>
        <button type="submit" className="button primary" disabled={status.state === "loading"}>
          <User size={18} aria-hidden="true" />
          <span>{status.state === "loading" ? "Signing in" : "Sign in"}</span>
        </button>
        {status.message ? <p className={`form-status ${status.state}`}>{status.message}</p> : null}
      </form>
    </section>
  );
}

function NotFound({ navigate }) {
  return (
    <section className="not-found">
      <p className="eyebrow">404</p>
      <h1>That page is not on the menu.</h1>
      <p>Use the preserved sitemap routes to find the current Gelato Spot content.</p>
      <ActionLink href="/" navigate={navigate} icon={ArrowRight}>Back home</ActionLink>
    </section>
  );
}

const EMAIL_SIGNUP_CONSENT =
  "Yes, email me occasional news, specials, and events from The Gelato Spot. I can unsubscribe at any time.";
const SMS_SIGNUP_CONSENT =
  "Yes, I agree to receive recurring automated marketing text messages with news, specials, and events from The Gelato Spot Wood Fired Pizzeria at the mobile number provided. Message frequency varies. Message and data rates may apply. Reply STOP to unsubscribe or HELP for help. Consent is not a condition of purchase.";

function createSubscriberState() {
  return {
    name: "",
    email: "",
    phone: "",
    emailConsent: false,
    smsConsent: false,
    company: ""
  };
}

function SkyTabOrderHandoff() {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(createSubscriberState);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, siteKey: "" });
  const dialogRef = useRef(null);

  useEffect(() => {
    let active = true;
    loadAppConfig()
      .then((config) => {
        if (active) setRecaptchaConfig(config.recaptcha || { enabled: false, siteKey: "" });
      })
      .catch(() => {
        if (active) setRecaptchaConfig({ enabled: false, siteKey: "" });
      });

    const interceptSkyTabOrder = (event) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const link = event.target.closest?.("a[href]");
      const href = link?.href || "";
      if (href !== contact.orderUrl && !href.startsWith(`${contact.orderUrl}?`)) {
        return;
      }

      event.preventDefault();
      setStatus({ state: "idle", message: "" });
      setOpen(true);
    };

    document.addEventListener("click", interceptSkyTabOrder, true);
    return () => {
      active = false;
      document.removeEventListener("click", interceptSkyTabOrder, true);
    };
  }, []);

  useLayoutEffect(() => {
    if (!open) return undefined;

    const previousActiveElement = document.activeElement;
    const focusInitial = () => dialogRef.current?.querySelector("button, input, a[href]")?.focus?.({ preventScroll: true });
    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        setOpen(false);
        return;
      }
      if (event.key !== "Tab") return;

      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(
          'a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex="-1"])'
        ) || []
      ).filter((element) => element.offsetParent !== null);
      if (!focusable.length) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown);
    document.documentElement.classList.add("modal-open");
    document.body.classList.add("modal-open");
    const focusTimer = window.setTimeout(focusInitial, 0);

    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener("keydown", onKeyDown);
      document.documentElement.classList.remove("modal-open");
      document.body.classList.remove("modal-open");
      previousActiveElement?.focus?.();
    };
  }, [open]);

  const update = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const continueToSkyTab = () => {
    window.location.assign(contact.orderUrl);
  };

  const joinAndContinue = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Saving your email permission..." });

    try {
      const recaptchaToken =
        recaptchaConfig.enabled && recaptchaConfig.siteKey
          ? await executeRecaptcha(recaptchaConfig.siteKey, RECAPTCHA_SUBSCRIBER_ACTION)
          : "";
      const query = new URLSearchParams(window.location.search);
      const attribution = Object.fromEntries(
        ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"]
          .map((key) => [key, query.get(key) || ""])
          .filter(([, value]) => value)
      );
      const metaEventId = createMetaEventId();
      const response = await fetch("/api/subscribers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          emailConsent: form.emailConsent,
          smsConsent: false,
          company: form.company,
          source: "order_handoff",
          attribution,
          metaEventId,
          recaptchaToken,
          recaptchaAction: RECAPTCHA_SUBSCRIBER_ACTION
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to save your email signup.");
      }

      await trackMetaEvent(
        "Lead",
        { content_name: "SkyTab order handoff email signup", content_category: "Email subscriber" },
        { eventId: metaEventId, server: false }
      );
      continueToSkyTab();
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  if (!open || typeof document === "undefined") return null;

  return createPortal(
    <div
      className="modal-backdrop order-handoff-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) setOpen(false);
      }}
    >
      <div
        ref={dialogRef}
        className="order-handoff-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="order-handoff-title"
        tabIndex={-1}
      >
        <button type="button" className="modal-close" onClick={() => setOpen(false)} aria-label="Close online ordering popup">
          <X size={20} aria-hidden="true" />
        </button>
        <div className="order-handoff-intro">
          <p className="eyebrow">Online ordering</p>
          <h2 id="order-handoff-title">Ready for pizza and gelato?</h2>
          <p>
            By continuing to SkyTab, you agree to our{" "}
            <a href="/terms-and-conditions/" target="_blank" rel="noreferrer">Terms and Conditions</a>{" "}
            and acknowledge our{" "}
            <a href="/privacy-policy/" target="_blank" rel="noreferrer">Privacy Policy</a>. Online ordering is provided by SkyTab and is also subject to its terms.
          </p>
        </div>

        <button type="button" className="button primary order-handoff-continue" onClick={continueToSkyTab}>
          <ShoppingBag size={18} aria-hidden="true" />
          <span>Continue to SkyTab</span>
          <ExternalLink size={16} aria-hidden="true" />
        </button>

        <div className="order-handoff-divider"><span>Optional email signup</span></div>
        <form className="order-handoff-form" onSubmit={joinAndContinue}>
          <div className="order-handoff-form-copy">
            <h3>Get occasional Gelato Spot updates</h3>
            <p>Email signup is optional and is not required to place an order.</p>
          </div>
          <div className="order-handoff-fields">
            <label>
              <span>Name</span>
              <input
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                autoComplete="name"
                required
              />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => update("email", event.target.value)}
                autoComplete="email"
                inputMode="email"
                required
              />
            </label>
          </div>
          <label className="order-handoff-consent">
            <input
              type="checkbox"
              checked={form.emailConsent}
              onChange={(event) => update("emailConsent", event.target.checked)}
              required
            />
            <span>{EMAIL_SIGNUP_CONSENT}</span>
          </label>
          <label className="join-honeypot" aria-hidden="true">
            <span>Company</span>
            <input value={form.company} onChange={(event) => update("company", event.target.value)} tabIndex="-1" autoComplete="off" />
          </label>
          <button type="submit" className="button outline order-handoff-join" disabled={status.state === "loading"}>
            <Mail size={18} aria-hidden="true" />
            <span>{status.state === "loading" ? "Saving permission..." : "Join updates and continue"}</span>
          </button>
          {status.message ? <p className={`form-status ${status.state}`} role="status">{status.message}</p> : null}
        </form>
      </div>
    </div>,
    document.body
  );
}

function JoinPage({ navigate, routeSearch = "" }) {
  const [form, setForm] = useState(createSubscriberState);
  const [status, setStatus] = useState({ state: "idle", message: "" });
  const [recaptchaConfig, setRecaptchaConfig] = useState({ enabled: false, siteKey: "" });
  const query = useMemo(() => new URLSearchParams(routeSearch), [routeSearch]);

  useEffect(() => {
    let active = true;
    loadAppConfig()
      .then((config) => {
        if (active) setRecaptchaConfig(config.recaptcha || { enabled: false, siteKey: "" });
      })
      .catch(() => {
        if (active) setRecaptchaConfig({ enabled: false, siteKey: "" });
      });
    return () => {
      active = false;
    };
  }, []);

  const update = (field, value) => {
    setForm((current) => ({
      ...current,
      [field]: value,
      ...(field === "smsConsent" && !value ? { phone: "" } : {})
    }));
  };

  const submit = async (event) => {
    event.preventDefault();
    setStatus({ state: "loading", message: "Saving your preferences..." });

    try {
      const metaEventId = createMetaEventId();
      const recaptchaToken =
        recaptchaConfig.enabled && recaptchaConfig.siteKey
          ? await executeRecaptcha(recaptchaConfig.siteKey, RECAPTCHA_SUBSCRIBER_ACTION)
          : "";
      const attribution = Object.fromEntries(
        ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term", "fbclid"]
          .map((key) => [key, query.get(key) || ""])
          .filter(([, value]) => value)
      );
      const response = await fetch("/api/subscribers", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...form,
          source: query.get("src") || attribution.utm_source || "website",
          attribution,
          metaEventId,
          recaptchaToken,
          recaptchaAction: RECAPTCHA_SUBSCRIBER_ACTION
        })
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.message || "Unable to save your signup.");
      }

      await trackMetaEvent(
        "Lead",
        {
          content_name: "Gelato Spot updates signup",
          content_category: form.smsConsent ? "Email and SMS subscriber" : "Email subscriber"
        },
        { eventId: metaEventId, server: false }
      );
      setStatus({
        state: "success",
        message: "You are on the list. Watch for occasional Gelato Spot updates."
      });
      setForm(createSubscriberState());
    } catch (error) {
      setStatus({ state: "error", message: error.message });
    }
  };

  return (
    <section className="join-page">
      <div className="join-story">
        <p className="eyebrow">Stay in the scoop</p>
        <h1>Good pizza. Fresh gelato. Worth hearing about.</h1>
        <p className="join-lede">
          Get occasional updates about flavors, specials, events, and what is coming out of the wood-fired oven at 32nd Street and Shea.
        </p>
        <div className="join-benefits" aria-label="What subscribers receive">
          <span><Star size={18} aria-hidden="true" /> New and returning flavors</span>
          <span><ShoppingBag size={18} aria-hidden="true" /> Specials and restaurant news</span>
          <span><Clock size={18} aria-hidden="true" /> Events and seasonal updates</span>
        </div>
        <p className="join-promise">No daily blasts. No purchased lists. Just Gelato Spot updates you asked for.</p>
      </div>

      <div className="join-card">
        {status.state === "success" ? (
          <div className="join-success" role="status">
            <div className="join-success-icon"><Mail size={28} aria-hidden="true" /></div>
            <p className="eyebrow">You are in</p>
            <h2>Thanks for joining us.</h2>
            <p>{status.message}</p>
            <div className="button-row">
              <ActionLink href="/menu/" navigate={navigate} icon={ArrowRight}>Browse the menu</ActionLink>
              <a className="button outline" href="/review" target="_blank" rel="noreferrer">
                <Star size={18} aria-hidden="true" /> Leave a review
              </a>
            </div>
          </div>
        ) : (
          <form className="join-form" onSubmit={submit}>
            <div>
              <p className="eyebrow">Join free</p>
              <h2>Tell us where to reach you.</h2>
            </div>
            <label>
              <span>Name</span>
              <input
                value={form.name}
                onChange={(event) => update("name", event.target.value)}
                autoComplete="name"
                required
              />
            </label>
            <label>
              <span>Email</span>
              <input
                type="email"
                value={form.email}
                onChange={(event) => update("email", event.target.value)}
                autoComplete="email"
                inputMode="email"
                required
              />
            </label>
            <label className="join-consent-row">
              <input
                type="checkbox"
                checked={form.emailConsent}
                onChange={(event) => update("emailConsent", event.target.checked)}
                required
              />
              <span>{EMAIL_SIGNUP_CONSENT}</span>
            </label>
            <label className="join-consent-row">
              <input
                type="checkbox"
                checked={form.smsConsent}
                onChange={(event) => update("smsConsent", event.target.checked)}
              />
              <span>{SMS_SIGNUP_CONSENT}</span>
            </label>
            {form.smsConsent ? (
              <label>
                <span>Mobile phone</span>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={(event) => update("phone", event.target.value)}
                  autoComplete="tel"
                  inputMode="tel"
                  required
                />
              </label>
            ) : null}
            <label className="join-honeypot" aria-hidden="true">
              <span>Company</span>
              <input value={form.company} onChange={(event) => update("company", event.target.value)} tabIndex="-1" autoComplete="off" />
            </label>
            <button className="button primary join-submit" type="submit" disabled={status.state === "loading"}>
              <Mail size={18} aria-hidden="true" />
              <span>{status.state === "loading" ? "Joining..." : "Join Gelato Spot updates"}</span>
            </button>
            <p className="join-privacy">
              We record your permission and signup source. Read our <AppLink href="/terms-and-conditions/" navigate={navigate}>Text Terms</AppLink> and <AppLink href="/privacy-policy/" navigate={navigate}>Privacy Policy</AppLink>.
            </p>
            {status.message ? <p className={`form-status ${status.state}`} role="status">{status.message}</p> : null}
          </form>
        )}
      </div>
    </section>
  );
}

export default function App() {
  const { path, displayPath, search, transitionStage, navigate } = useRoute();

  const page = (() => {
    if (isMenuItemPath(displayPath)) {
      return <MenuItemPage path={displayPath} navigate={navigate} />;
    }

    switch (displayPath) {
      case "/":
        return <HomePage navigate={navigate} />;
      case "/menu/":
        return <MenuPage navigate={navigate} routeSearch={search} />;
      case "/location/":
        return <LocationPage navigate={navigate} />;
      case "/about-us/":
        return <AboutPage />;
      case "/catering/":
        return <CateringPage navigate={navigate} />;
      case "/join/":
        return <JoinPage navigate={navigate} routeSearch={search} />;
      case "/frequently-asked-questions-faq/":
        return <FAQPage navigate={navigate} />;
      case "/privacy-policy/":
        return <LegalPage slug="privacy-policy" />;
      case "/terms-and-conditions/":
        return <LegalPage slug="terms-and-conditions" />;
      case "/developers/":
        return <DevelopersPage navigate={navigate} />;
      case "/preferences/":
        return <PreferencesPage routeSearch={search} />;
      case "/sign-in/":
        return <SignInPage />;
      case "/admin/":
        return <AdminPage />;
      default:
        return <NotFound navigate={navigate} />;
    }
  })();

  return (
    <>
      <Meta path={path} />
      <MetaEvents path={path} search={search} />
      <SkyTabOrderHandoff />
      <Header path={path} navigate={navigate} />
      <main id="content" className={`route-${transitionStage}`}>
        <div key={displayPath} className={`page-stage page-stage-${transitionStage}`}>
          {page}
        </div>
      </main>
      <Footer navigate={navigate} />
    </>
  );
}
